import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { updateClubDetails, getUserTypes, getClubDetails, updateUserTypes, getCourts, getBookingCategories, updateCourts, updateBookingCategories, getBookingOptions, updateBookingOptions, getEmailTemplates, updateEmailTemplates, testShellySwitch, getClubTerms, refreshToken } from '../api';
import { PlusIcon, MinusIcon, EyeIcon, EyeSlashIcon, PencilIcon, UserIcon, Cog6ToothIcon } from '@heroicons/react/24/solid';
import { FaWhatsapp } from 'react-icons/fa6';
import ColorPicker, { TAILWIND_COLORS } from '../components/ColorPicker';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import PermissionsModal from '../components/PermissionsModal';
import MembershipModal from '../components/MembershipModal';
import EmailTemplatePreviewModal from '../components/EmailTemplatePreviewModal';
import ErrorModal from '../components/ErrorModal';
import EmailTemplateEditModal from '../components/EmailTemplateEditModal';
import ConfirmationModal from '../components/ConfirmationModal';
import SwitchesModal from '../components/SwitchesModal';
import BookingModal from '../components/BookingModal';
import ClubTermsModal from '../components/ClubTermsModal';
import ClubTermsEditModal from '../components/ClubTermsEditModal';
import { formatSportDisplayName, normalizeSportNameInput, sanitizeSportNameForTyping, toSportKey } from '../utils/sportName';
import {
  hasOnlineSignupPrivilegedAccessConflict,
  isPrivilegedPermissionBlockedByOnlineSignup,
  PRIVILEGED_ACCESS_BLOCKED_BY_ONLINE_SIGNUP_MESSAGE,
  userTypeAllowsOnlineSignup,
  wouldGrantPrivilegedClubAccess
} from '../utils/userTypePermissions';

/** Minutes from stored club record → number for the form, or '' if missing/invalid. */
const normalizeAccessControlMinutesFromStorage = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  return Math.round(n);
};

const parseAccessControlMinutesForSave = (raw) => {
  if (raw === '' || raw === undefined || raw === null) {
    return { ok: false, message: 'Show before booking starts (mins) is required.' };
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    return {
      ok: false,
      message: 'Show before booking starts (mins) must be a non-negative whole number.',
    };
  }
  return { ok: true, value: n };
};

/** Whole minutes only: empty, or non‑negative integer from `type="number"` (paste/decimals rounded). */
function normalizeAccessControlMinutesDigitsInput(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n < 0) return '';
  return Math.round(n);
}

function isAccessControlMinutesValueEmpty(v) {
  return (
    v === '' ||
    v === undefined ||
    v === null ||
    (typeof v === 'number' && Number.isNaN(v))
  );
}

/** `type="number"` still allows `e/E/+/-/.` while editing; block those for integer minutes (fee slot allows decimals). */
function handleAccessControlWholeMinutesKeyDown(e) {
  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
    e.preventDefault();
  }
}

const normalizeAccessControlChannel = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  return digits === '' ? '0' : String(Number(digits));
};

/** Parse `accessControl` from API (JSON string or object). */
const parseAccessControlFromApi = (raw) => {
  if (raw == null || raw === '') return null;
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (obj.type === 'none') return null;
  if (obj.type === 'access_code') {
    return {
      type: 'access_code',
      code: String(obj.code ?? ''),
      minutesBeforeStart: normalizeAccessControlMinutesFromStorage(obj.minutesBeforeStart),
    };
  }
  if (obj.type === 'shelly') {
    const deviceIdRaw = String(obj.deviceId ?? '');
    const deviceIdTrimmed = deviceIdRaw.trim();
    return {
      type: 'shelly',
      deviceId: deviceIdRaw,
      channel: deviceIdTrimmed ? normalizeAccessControlChannel(obj.channel) : '',
      minutesBeforeStart: normalizeAccessControlMinutesFromStorage(obj.minutesBeforeStart),
    };
  }
  return null;
};

const getAccessControlSelectMode = (ac) => {
  if (!ac || typeof ac !== 'object') return 'none';
  if (ac.type === 'access_code') return 'access_code';
  if (ac.type === 'shelly') return 'shelly';
  return 'none';
};

const getAccessControlMethodDescription = (ac) => {
  const mode = getAccessControlSelectMode(ac);
  if (mode === 'access_code') {
    return 'Show the Access Code to the user during their booking. You can also show it before the booking starts but only if the booking can no longer be cancelled by the user (see Cancellation cutoff in User Type Permissions)';
  }
  if (mode === 'shelly') {
    return 'Show an Open Gate button to the user during their booking. Requires Shelly Cloud API and WIFI at the club. You can also show it before the booking starts but only if the booking can no longer be cancelled by the user (see Cancellation cutoff in User Type Permissions)';
  }
  return 'Provide access instructions in the Booking Confirmation email template or select an Access Control method for users to gain access to the club from the booking. ';
};

const composeAccessControlShellyDeviceId = (deviceId, channel) => {
  const id = String(deviceId || '').trim();
  if (!id) return '';
  return `${id}:${normalizeAccessControlChannel(channel)}`;
};

const formatAccessControlShellyTestMessage = (deviceStatus) => {
  if (!deviceStatus) return 'Unable to verify this switch. Please try again.';
  if (deviceStatus.error) return deviceStatus.error;
  if (!deviceStatus.exists) return 'Device is not registered in Shelly Cloud.';
  if (!deviceStatus.online) return 'Device is registered but the device is OFFLINE.';
  if (!deviceStatus.output) return 'Device is ONLINE but turned OFF.';
  if (deviceStatus.timerDuration !== null && deviceStatus.timerDuration !== undefined) {
    return `Device is ON and set to turn off in ${deviceStatus.timerDuration} minute${deviceStatus.timerDuration !== 1 ? 's' : ''}.`;
  }
  return 'Device is ON.';
};

/** Courts table/cards for one sport (Court Details tab). */
function SportCourtsEditor({
  sport,
  courts,
  thClass,
  tdClass,
  timeInputClass,
  getCourtDisplayLabel,
  handleCourtChange,
  onEditCourtDisplayName,
  handleOpenSwitchesModal,
  addCourt,
  removeLastCourt,
  iconButtonClass,
  getSportDisplayName,
  canEditClubConfig,
}) {
  const sportCourtsList = courts.filter(court => court.court.startsWith(sport));
  const courtControls = (
    <div className="mt-6 flex justify-start">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => addCourt(sport)}
          className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`}
          title="Add Court"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => removeLastCourt(sport)}
          className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`}
          title="Remove Last Court"
          disabled={sportCourtsList.length === 0}
        >
          <MinusIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  if (sportCourtsList.length === 0) {
    return (
      <>
        <div className="text-center py-8 text-slate-500">
          Add courts for {getSportDisplayName(sport)}
        </div>
        {courtControls}
      </>
    );
  }

  return (
    <>
      <div className="md:hidden space-y-3">
        {sportCourtsList.map((court) => (
          <div key={court.court} className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-3">
            <button
              type="button"
              onClick={() => canEditClubConfig && onEditCourtDisplayName(court.court)}
              disabled={!canEditClubConfig}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${canEditClubConfig ? 'text-slate-700 hover:text-blue-700' : 'text-slate-700'} disabled:cursor-default`}
              title={canEditClubConfig ? 'Edit display name' : undefined}
            >
              <span>{getCourtDisplayLabel(court)}</span>
              {canEditClubConfig && <PencilIcon className="h-3.5 w-3.5" />}
            </button>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Opening Time</label>
                <input type="time" value={court.openingTime} onChange={e => handleCourtChange(court.court, 'openingTime', e.target.value)} className={timeInputClass} style={{ maxWidth: '100%' }} step="1800" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Closing Time</label>
                <input type="time" value={court.closingTime} onChange={e => handleCourtChange(court.court, 'closingTime', e.target.value)} className={timeInputClass} style={{ maxWidth: '100%' }} step="1800" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Slot Duration</label>
                <select value={court.slotDuration} onChange={e => handleCourtChange(court.court, 'slotDuration', e.target.value)} className={timeInputClass} style={{ maxWidth: '100%' }}>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Floodlights</label>
                <label className="inline-flex items-center mt-1 cursor-pointer">
                  <input type="checkbox" checked={court.floodlights} onChange={e => handleCourtChange(court.court, 'floodlights', e.target.checked)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 relative" />
                </label>
              </div>
            </div>
            {court.floodlights && (
              <div className="flex items-center gap-3 pt-1 border-t border-slate-200">
                <button type="button" onClick={() => handleOpenSwitchesModal(court)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-xs font-medium rounded-lg text-slate-600 bg-white hover:bg-slate-50" title="Configure Shelly Switches">
                  <Cog6ToothIcon className="h-4 w-4 shrink-0" />
                  Switches
                </button>
                <span className="text-xs text-slate-500">{court.switches?.length > 0 ? `${court.switches.length} configured` : 'Not configured'}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <div className="min-w-full">
          <div className="grid grid-cols-6 bg-slate-50">
            <div className={`${thClass} text-left pl-6`}>COURT</div>
            <div className={`${thClass} text-center`}>OPENING TIME</div>
            <div className={`${thClass} text-center`}>CLOSING TIME</div>
            <div className={`${thClass} text-center`}>SLOT DURATION</div>
            <div className={`${thClass} text-center`}>FLOODLIGHTS</div>
            <div className={`${thClass} text-center`}>SWITCHES</div>
          </div>
          <div className="bg-white divide-y divide-slate-200">
            {sportCourtsList.map((court) => (
              <div key={court.court} className="grid grid-cols-6 items-center">
                <div className={`${tdClass} text-left pl-6 font-medium text-slate-600`}>
                  <button
                    type="button"
                    onClick={() => canEditClubConfig && onEditCourtDisplayName(court.court)}
                    disabled={!canEditClubConfig}
                    className={`inline-flex items-center gap-1.5 ${canEditClubConfig ? 'hover:text-blue-700' : ''} disabled:cursor-default`}
                    title={canEditClubConfig ? 'Edit display name' : undefined}
                  >
                    <span>{getCourtDisplayLabel(court)}</span>
                    {canEditClubConfig && <PencilIcon className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <div className={`${tdClass} text-center`}>
                  <input type="time" value={court.openingTime} onChange={e => handleCourtChange(court.court, 'openingTime', e.target.value)} className={timeInputClass} step="1800" />
                </div>
                <div className={`${tdClass} text-center`}>
                  <input type="time" value={court.closingTime} onChange={e => handleCourtChange(court.court, 'closingTime', e.target.value)} className={timeInputClass} step="1800" />
                </div>
                <div className={`${tdClass} text-center`}>
                  <select value={court.slotDuration} onChange={e => handleCourtChange(court.court, 'slotDuration', e.target.value)} className={timeInputClass}>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </div>
                <div className={`${tdClass} text-center`}>
                  <div className="flex justify-center">
                    <label className="inline-flex relative items-center cursor-pointer">
                      <input type="checkbox" checked={court.floodlights} onChange={e => handleCourtChange(court.court, 'floodlights', e.target.checked)} className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>
                </div>
                <div className={`${tdClass} text-center`}>
                  {court.floodlights ? (
                    <div className="flex flex-col items-center gap-1">
                      <button type="button" onClick={() => handleOpenSwitchesModal(court)} className="inline-flex items-center p-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Configure Shelly Switches">
                        <Cog6ToothIcon className="h-4 w-4 shrink-0" />
                      </button>
                      <span className="text-xs text-slate-500">{court.switches?.length > 0 ? `${court.switches.length} ${court.switches.length === 1 ? 'switch' : 'switches'}` : 'Not configured'}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">Disabled</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {courtControls}
    </>
  );
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function parseOpeningMinutes(hhmm) {
  const [h, m] = String(hhmm || '0:0').split(':').map(Number);
  return h * 60 + (Number.isFinite(m) ? m : 0);
}

function minutesToHHMM(total) {
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

/** Next bookable slot on the lowest-numbered court for a sport (for booking modal preview). */
function buildPreviewBookingSlotData(sport, courtsList) {
  if (!sport || !Array.isArray(courtsList)) return null;
  const sorted = courtsList
    .filter(c => c?.court && String(c.court).split('-')[0] === sport)
    .sort((a, b) => {
      const na = parseInt(String(a.court).split('-')[1], 10) || 0;
      const nb = parseInt(String(b.court).split('-')[1], 10) || 0;
      return na - nb;
    });
  const courtRow = sorted[0];
  if (!courtRow) return null;

  const dur = parseInt(courtRow.slotDuration, 10) || 60;
  const openM = parseOpeningMinutes(courtRow.openingTime);
  const closeM = parseOpeningMinutes(courtRow.closingTime);
  const lastStart = closeM - dur;
  if (lastStart < openM) return null;

  const now = new Date();
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const curM = now.getHours() * 60 + now.getMinutes();

  let startM = openM;
  while (startM <= lastStart && startM <= curM) {
    startM += dur;
  }
  if (startM > lastStart) {
    day.setDate(day.getDate() + 1);
    startM = openM;
  }

  const ymd = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
  return {
    court: courtRow.court,
    date: ymd,
    startTime: minutesToHHMM(startM),
    endTime: minutesToHHMM(startM + dur)
  };
}

/** Plain text for email template API (avoids re-creating the parser on every render). */
const emailTemplateHtmlToText = (html) => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || '';
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n');
};

const ClubConfig = () => {
  const { user, logoutUser, loginUser } = useContext(AuthContext);
  const { clubDetails: savedClubDetails, setClubDetails: setSavedClubDetails, getCategoryStyle } = useClub();
  const navigate = useNavigate();
  const refreshAuthSession = async () => {
    try {
      const newToken = await refreshToken();
      if (newToken) {
        loginUser(newToken);
      }
    } catch (err) {
      console.error('Failed to refresh session after saving user types', err);
    }
  };
  const [activeTab, setActiveTab] = useState('');
  const [configTab, setConfigTab] = useState('club-details');
  const [errors, setErrors] = useState({});
  const [activeEditTemplate, setActiveEditTemplate] = useState(null);
  const [activePreviewTemplate, setActivePreviewTemplate] = useState(null);
  const [availableSports, setAvailableSports] = useState([]);
  const [isAddingSport, setIsAddingSport] = useState(false);
  const [newSportName, setNewSportName] = useState('');
  const [isCourtSaveRemovalModalOpen, setIsCourtSaveRemovalModalOpen] = useState(false);
  const [courtSaveRemovalSports, setCourtSaveRemovalSports] = useState([]);
  const [draggedSport, setDraggedSport] = useState(null);
  const courtDetailsSportsBaselineRef = useRef(null);
  const courtSaveSnapshotRef = useRef(null);
  /** True if last mousedown was on the upper native spinner (empty → step-up → 1 should become 0). */
  const accessControlMinutesSpinUpPendingRef = useRef(false);
  
  // Initialize with default values
  const defaultClubDetails = {
    name: '',
    location: '',
    googleMapEmbedUrl: '',
    contacts: [
      { id: 1, title: '', number: '' }
    ],
    emails: [
      { id: 1, title: '', address: '' }
    ],
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    xdotcom: '',
    youtube: '',
    paymentProvider: 'none', // 'none', 'zapper', 'yoco', or 'ikhokha'
    zapperMerchantId: '',
    zapperSiteId: '',
    zapperApiKey: '',
    yocoPublicKey: '',
    yocoSecretKey: '',
    ikhokhaAppKeyId: '',
    ikhokhaApiSecret: '',
    ikhokhaMode: 'test',
    shellyServerURL: '',
    shellyApiKey: '',
    /** Gate / door access — persisted as JSON on the club record. */
    accessControl: null,
    termsEnabled: false,
    termsEnforced: false,
    termsHtml: ''
  };

  const [clubDetails, setClubDetails] = useState(defaultClubDetails);
  const [courts, setCourts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isBookingPermissionPreviewOpen, setIsBookingPermissionPreviewOpen] = useState(false);
  const [bookingPreviewSlot, setBookingPreviewSlot] = useState(null);
  const [bookingPreviewUserTypeSnapshot, setBookingPreviewUserTypeSnapshot] = useState(null);
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [openColorPicker, setOpenColorPicker] = useState(null);

  // Add loading and error states for save buttons
  const [isSavingCategories, setIsSavingCategories] = useState(false);
  const [isSavingCourts, setIsSavingCourts] = useState(false);
  const [isSavingClubDetails, setIsSavingClubDetails] = useState(false);
  const [isSavingUserTypes, setIsSavingUserTypes] = useState(false);
  const [isSavingBookingOptions, setIsSavingBookingOptions] = useState(false);
  const [error, setError] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isSavingEmailTemplates, setIsSavingEmailTemplates] = useState(false);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showZapperApiKey, setShowZapperApiKey] = useState(false);
  const [showYocoSecretKey, setShowYocoSecretKey] = useState(false);
  const [showIkhokhaApiSecret, setShowIkhokhaApiSecret] = useState(false);
  const [showShellyApiKey, setShowShellyApiKey] = useState(false);
  const [showAccessControlCode, setShowAccessControlCode] = useState(false);
  const [accessControlShellyTest, setAccessControlShellyTest] = useState({ status: 'idle', message: '' });
  const [isSwitchesModalOpen, setIsSwitchesModalOpen] = useState(false);
  const [selectedCourtForSwitches, setSelectedCourtForSwitches] = useState(null);
  const [isTermsPreviewOpen, setIsTermsPreviewOpen] = useState(false);
  const [isTermsEditOpen, setIsTermsEditOpen] = useState(false);
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const [isCourtDisplayNameModalOpen, setIsCourtDisplayNameModalOpen] = useState(false);
  const [editingCourtId, setEditingCourtId] = useState('');
  const [courtDisplayNameDraft, setCourtDisplayNameDraft] = useState('');
  const [courtDisplayNameError, setCourtDisplayNameError] = useState('');

  const [floatingSaveTop, setFloatingSaveTop] = useState(96);
  const configTabBarRef = useRef(null);
  const baselinesRef = useRef({
    clubDetails: '{}',
    availableSports: '[]',
    courts: '[]',
    categories: '[]',
    bookingOptions: '[]',
    userTypes: '[]',
    emailTemplates: '[]',
    terms: '{}'
  });

  const createDefaultSportPermissions = () => ({
    canBook: false,
    feePerSlot: 0,
    canOperateLights: false,
    lightsFeePerMinute: 0,
    canBookExtras: false,
    bookingExtras: [],
    canBookMultipleCourts: false,
    canBookRecurring: [],
    advanceBookingLimit: -1,
    cancellationCutoffHours: 0,
    bookingCategories: [],
    canMakePublicBookings: false
  });

  const getOrderedSportsFromCourts = (courtsList = []) => {
    const sportIndexes = courtsList.reduce((acc, court) => {
      const sport = court.court.split('-')[0];
      const index = Number(court.sportIndex);
      if (!Number.isFinite(index)) return acc;
      if (acc[sport] === undefined || index < acc[sport]) {
        acc[sport] = index;
      }
      return acc;
    }, {});

    const sports = [...new Set(courtsList.map(court => court.court.split('-')[0]))];
    return [...sports].sort((a, b) => {
      const aIndex = sportIndexes[a];
      const bIndex = sportIndexes[b];
      if (aIndex === undefined && bIndex === undefined) {
        return a.localeCompare(b);
      }
      if (aIndex === undefined) return 1;
      if (bIndex === undefined) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.localeCompare(b);
    });
  };

  const toStableJson = (value) => JSON.stringify(value ?? []);
  const pickTermsState = (source) => ({
    termsEnabled: Boolean(source?.termsEnabled),
    termsEnforced: Boolean(source?.termsEnforced),
    termsHtml: source?.termsHtml || ''
  });
  const pickClubDetailsCoreState = (source) => {
    if (!source || typeof source !== 'object') return {};
    const { termsEnabled, termsEnforced, termsHtml, hasPublishedMemberships: _hasPublishedMemberships, ...rest } = source;
    const normalizedContacts = Array.isArray(rest.contacts)
      ? rest.contacts
          .filter((c) => {
            const phone = c?.phone || '';
            const email = c?.email || '';
            return (phone && phone.trim()) || (email && email.trim());
          })
          .map((c) => ({ ...c }))
      : [];
    return {
      ...rest,
      contacts: normalizedContacts
    };
  };
  const normalizeCourtsForComparison = (courtsList = [], sportsList = availableSports) =>
    (Array.isArray(courtsList) ? courtsList : []).map((court) => {
      const sport = String(court?.court || '').split('-')[0];
      return {
        ...court,
        sportIndex: sportsList.indexOf(sport),
        switches: Array.isArray(court?.switches)
          ? court.switches.filter((deviceId) => deviceId && deviceId.trim() !== '')
          : []
      };
    });

  const normalizeDisplayName = (value) => String(value || '').trim().replace(/\s+/g, ' ');

  const getCourtDisplayLabel = (court) => {
    const displayName = normalizeDisplayName(court?.displayName);
    if (displayName) return displayName;
    const number = String(court?.court || '').split('-')[1];
    return `Court ${number || ''}`.trim();
  };

  const validateDisplayNameUniqueness = (courtsList, targetCourtId, rawDisplayName) => {
    const normalized = normalizeDisplayName(rawDisplayName);
    if (!normalized) return null;
    const normalizedKey = normalized.toLocaleLowerCase();
    const duplicate = (Array.isArray(courtsList) ? courtsList : []).find((court) => (
      court.court !== targetCourtId &&
      normalizeDisplayName(court.displayName).toLocaleLowerCase() === normalizedKey
    ));
    if (duplicate) {
      return `Display name "${normalized}" is already used. Please choose a unique name.`;
    }
    return null;
  };

  const setBaseline = (key, value) => {
    baselinesRef.current[key] = toStableJson(value);
  };

  const commitSavedClubDetailsToState = (detailsToSave) => {
    setSavedClubDetails(detailsToSave);
    setBaseline('clubDetails', pickClubDetailsCoreState(detailsToSave));
    setBaseline('terms', pickTermsState(detailsToSave));
  };

  const hasUnsavedChanges = {
    clubDetails: () =>
      baselinesRef.current.clubDetails !== toStableJson(pickClubDetailsCoreState(clubDetails)),
    courtDetails: () =>
      baselinesRef.current.availableSports !== toStableJson(availableSports) ||
      baselinesRef.current.courts !== toStableJson(normalizeCourtsForComparison(courts, availableSports)),
    bookingOptions: () =>
      baselinesRef.current.bookingOptions !== toStableJson(bookingOptions),
    bookingCategories: () =>
      baselinesRef.current.categories !== toStableJson(categories),
    userTypes: () =>
      baselinesRef.current.userTypes !== toStableJson(userTypes),
    emailTemplates: () =>
      baselinesRef.current.emailTemplates !== toStableJson(emailTemplates),
    clubTerms: () =>
      baselinesRef.current.terms !== toStableJson(pickTermsState(clubDetails)),
  };

  const dirtyTabs = {
    'club-details': hasUnsavedChanges.clubDetails(),
    'court-details': hasUnsavedChanges.courtDetails(),
    'booking-options': hasUnsavedChanges.bookingOptions(),
    'booking-categories': hasUnsavedChanges.bookingCategories(),
    'user-types': hasUnsavedChanges.userTypes(),
    'email-templates': hasUnsavedChanges.emailTemplates(),
    'club-terms': hasUnsavedChanges.clubTerms(),
  };
  const totalDirtyCount = Object.values(dirtyTabs).filter(Boolean).length;

  const normalizePermissionsObject = (permissions = {}) => {
    const normalized = { ...permissions };
    Object.keys(normalized).forEach((key) => {
      const perm = normalized[key];
      if (
        perm &&
        typeof perm === 'object' &&
        (perm.canBook !== undefined ||
          perm.canOperateLights !== undefined ||
          perm.canBookLights !== undefined)
      ) {
        normalized[key] = {
          ...perm,
          canOperateLights: perm.canOperateLights ?? perm.canBookLights ?? false,
          lightsFeePerMinute:
            perm.lightsFeePerMinute ?? perm.feePerLightsToken ?? 0
        };
        delete normalized[key].canBookLights;
        delete normalized[key].feePerLightsToken;
      }
    });
    return normalized;
  };

  const normalizeUserType = (userType) => ({
    ...userType,
    permissions: normalizePermissionsObject(userType.permissions || {})
  });

  const syncUserTypesWithBookingOptions = (types, options) => {
    const validOptionIds = new Set(
      (options || [])
        .map(option => option?.option)
        .filter(Boolean)
    );

    return (types || []).map((type) => {
      const permissions = type.permissions || {};
      let hasChanges = false;
      const syncedPermissions = {};

      Object.keys(permissions).forEach((permissionKey) => {
        const permissionValue = permissions[permissionKey];
        if (!permissionValue || typeof permissionValue !== 'object') {
          syncedPermissions[permissionKey] = permissionValue;
          return;
        }

        const currentExtras = Array.isArray(permissionValue.bookingExtras)
          ? permissionValue.bookingExtras
          : [];
        const filteredExtras = currentExtras.filter(extra =>
          validOptionIds.has(extra?.option)
        );

        if (filteredExtras.length !== currentExtras.length) {
          hasChanges = true;
          syncedPermissions[permissionKey] = {
            ...permissionValue,
            bookingExtras: filteredExtras
          };
          return;
        }

        syncedPermissions[permissionKey] = permissionValue;
      });

      return hasChanges ? { ...type, permissions: syncedPermissions } : type;
    });
  };

  // Use effect to initialize form with saved club details
  useEffect(() => {
    if (!user?.permissions?.clubConfiguration?.canView) {
      setError('You do not have permissions to view this page');
      setIsErrorModalOpen(true);
      return;
    }

    if (savedClubDetails && Object.keys(savedClubDetails).length > 0) {
      // If contacts is missing or empty, default to one phone and one email contact
      let contacts = Array.isArray(savedClubDetails.contacts) && savedClubDetails.contacts.length > 0
        ? savedClubDetails.contacts
        : [
            { name: '', phone: '' },
            { name: '', email: '' }
          ];
      setClubDetails({
        ...defaultClubDetails,
        ...savedClubDetails,
        contacts,
        accessControl: parseAccessControlFromApi(savedClubDetails.accessControl),
        termsEnabled: savedClubDetails.termsEnabled || false,
        termsEnforced: savedClubDetails.termsEnforced || false,
        termsHtml: savedClubDetails.termsHtml || ''
      });
    }
  }, [savedClubDetails]);

  // Fetch club details, user types, courts and categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsDataLoading(true);
        const [club, clubTerms, userTypesData, courtsData, categoriesData, bookingOptionsData, emailTemplatesData] = await Promise.all([
          getClubDetails(),
          getClubTerms(),
          getUserTypes(),
          getCourts(),
          getBookingCategories(),
          getBookingOptions(),
          getEmailTemplates()
        ]);
        
        // Club details logic
        let contacts = Array.isArray(club.contacts) && club.contacts.length > 0
          ? club.contacts
          : [
              { name: '', phone: '' },
              { name: '', email: '' }
            ];
        // Load terms flags from clubDetails and termsHtml from getClubTerms
        setClubDetails({
          ...defaultClubDetails,
          ...club,
          contacts,
          accessControl: parseAccessControlFromApi(club.accessControl),
          termsEnabled: club.termsEnabled || false,
          termsEnforced: club.termsEnforced || false,
          termsHtml: clubTerms?.html || ''
        });
        const normalizedUserTypes = userTypesData.map(normalizeUserType);
        setUserTypes(normalizedUserTypes);
        const normalizedCourts = courtsData.map((court) => ({
          ...court,
          displayName: normalizeDisplayName(court.displayName),
          switches: Array.isArray(court.switches) ? court.switches : []
        }));
        setCourts(normalizedCourts);
        const categoriesNormalized = (categoriesData || []).map((category) => {
          const rawMultiplier = category?.feeMultiplier ?? category?.feeMuliplier ?? 1;
          const parsedMultiplier = Number(rawMultiplier);
          return {
            ...category,
            feeMultiplier: Number.isFinite(parsedMultiplier) && parsedMultiplier >= 0 ? parsedMultiplier : 1
          };
        });
        setCategories(categoriesNormalized);
        setBookingOptions(bookingOptionsData);
        setEmailTemplates(emailTemplatesData);

        // Extract available sports from courts data
        const sportsFromCourts = getOrderedSportsFromCourts(normalizedCourts);
        const validSports = sportsFromCourts.length > 0 ? sportsFromCourts : ['tennis'];
        setAvailableSports(validSports);
        courtDetailsSportsBaselineRef.current = [...validSports];
        setBaseline('availableSports', validSports);
        setBaseline('courts', normalizeCourtsForComparison(normalizedCourts, validSports));
        setBaseline('categories', categoriesNormalized);
        setBaseline('bookingOptions', bookingOptionsData);
        setBaseline('userTypes', userTypesData);
        setBaseline('clubDetails', pickClubDetailsCoreState({
          ...defaultClubDetails,
          ...club,
          contacts,
          accessControl: parseAccessControlFromApi(club.accessControl),
        }));
        setBaseline('emailTemplates', emailTemplatesData);
        setBaseline('terms', pickTermsState({
          ...club,
          termsHtml: clubTerms?.html || ''
        }));

        // Ensure activeTab is valid
        if (!validSports.includes(activeTab) && validSports.length > 0) {
          setActiveTab(validSports[0]);
        }

        console.log('************* clubDetails', club);
        console.log('************* userTypesData', userTypesData);
        console.log('************* courtsData', courtsData);
        console.log('************* categoriesData', categoriesData);
        console.log('************* bookingOptionsData', bookingOptionsData);
        console.log('************* availableSports', sportsFromCourts);

      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setIsDataLoading(false);
      }
    };
    fetchData();
  }, []);

  const getNextCourtNumber = (type) => {
    const sportCourts = courts.filter(court => court.court.startsWith(type));
    return sportCourts.length + 1;
  };

  const addNewSport = () => {
    const normalizedInput = normalizeSportNameInput(newSportName);
    if (!normalizedInput) {
      setIsAddingSport(false);
      setNewSportName('');
      return;
    }
    const sportKey = toSportKey(normalizedInput);
    if (!availableSports.includes(sportKey)) {
      const updatedSports = [...availableSports, sportKey];
      setAvailableSports(updatedSports);
      setActiveTab(sportKey);
      setIsAddingSport(false);
      setNewSportName('');

      // Add default categories for the new sport
      const defaultCategories = [
          {
            id: 'available',
            name: 'Available',
            colour: 'green',
            type: sportKey,
            isFixed: true,
            feeMultiplier: 1
          },
          {
            id: 'recurring',
            name: 'Recurring',
            colour: 'purple',
            type: sportKey,
            isFixed: true,
            feeMultiplier: 1
          },
          {
            id: '1',
            name: 'Category 1',
            colour: 'blue',
            type: sportKey,
            isFixed: false,
            feeMultiplier: 1
          }
        ];
        
        setCategories([...categories, ...defaultCategories]);
        
        // Update existing user types to include permissions for the new sport
        const updatedUserTypes = userTypes.map(userType => {
          if (userType.permissions[sportKey]) {
            return userType;
          }
          return normalizeUserType({
            ...userType,
            permissions: {
              ...userType.permissions,
              [sportKey]: createDefaultSportPermissions()
            }
          });
        });
        setUserTypes(updatedUserTypes);
        // One default court so the sport is valid to save (avoids "remove sport" prompts with zero courts)
        addCourt(sportKey);
    } else {
      handleError('That sport name is already in the list.');
      setIsAddingSport(false);
      setNewSportName('');
    }
  };

  //const removeSport = (sportToRemove) => {
  //  if (availableSports.length > 1) {
  //    setAvailableSports(availableSports.filter(sport => sport !== sportToRemove));
  //    if (activeTab === sportToRemove) {
  //      // Switch to the first available sport after removal
  //      const remainingSports = availableSports.filter(sport => sport !== sportToRemove);
  //      setActiveTab(remainingSports[0] || 'tennis');
  //    }
  //  }
  //};

  const getSportsRequiringRemovalOnCourtSave = (baselineSports, currentSports, courtsList) => {
    const currentSet = new Set(currentSports);
    const removedFromList =
      Array.isArray(baselineSports) && baselineSports.length > 0
        ? baselineSports.filter((s) => !currentSet.has(s))
        : [];
    const zeroCourts = currentSports.filter(
      (s) => !courtsList.some((c) => String(c.court || '').startsWith(s))
    );
    return [...new Set([...removedFromList, ...zeroCourts])];
  };

  const stageRemoveLastSport = () => {
    if (availableSports.length <= 1) {
      handleError('Cannot remove the last sport. At least one sport must remain.');
      return;
    }
    const lastSport = availableSports[availableSports.length - 1];
    const remainingSports = availableSports.filter((sport) => sport !== lastSport);

    const updatedUserTypes = userTypes.map((userType) => {
      const newPermissions = { ...userType.permissions };
      delete newPermissions[lastSport];
      return { ...userType, permissions: newPermissions };
    });
    setUserTypes(updatedUserTypes);
    setAvailableSports(remainingSports);
    setCategories(categories.filter((cat) => cat.type !== lastSport));
    setBookingOptions(bookingOptions.filter((option) => !option.option?.startsWith(`${lastSport}-`)));
    setCourts(courts.filter((court) => !court.court.startsWith(lastSport)));
    if (activeTab === lastSport) {
      setActiveTab(remainingSports[0] || '');
    }
  };

  const addCourt = (type) => {
    const nextId = getNextCourtNumber(type);
    const newCourt = {
      court: `${type}-${nextId}`,
      openingTime: '06:00',
      closingTime: '22:00',
      floodlights: false,
      slotDuration: '60',
      displayName: '',
      switches: []
    };

    setCourts([...courts, newCourt]);
  };

  const removeLastCourt = (type) => {
    const sportCourts = courts.filter(court => court.court.startsWith(type));
    if (sportCourts.length > 0) {
      const lastCourt = sportCourts[sportCourts.length - 1];
      setCourts(courts.filter(court => court.court !== lastCourt.court));
    }
  };

  const handleCourtChange = (courtId, field, value) => {
    setCourts(courts.map(court => {
      if (court.court !== courtId) return court;
      const updatedCourt = { ...court, [field]: value };
      if (field === 'floodlights' && value === false) {
        updatedCourt.switches = [];
      }
      return updatedCourt;
    }));
  };

  const handleOpenCourtDisplayNameModal = (courtId) => {
    const targetCourt = courts.find((court) => court.court === courtId);
    if (!targetCourt) return;
    setEditingCourtId(courtId);
    setCourtDisplayNameDraft(normalizeDisplayName(targetCourt.displayName));
    setCourtDisplayNameError('');
    setIsCourtDisplayNameModalOpen(true);
  };

  const handleCloseCourtDisplayNameModal = () => {
    setIsCourtDisplayNameModalOpen(false);
    setEditingCourtId('');
    setCourtDisplayNameDraft('');
    setCourtDisplayNameError('');
  };

  const handleSaveCourtDisplayName = () => {
    if (!editingCourtId) return;
    const validationError = validateDisplayNameUniqueness(courts, editingCourtId, courtDisplayNameDraft);
    if (validationError) {
      setCourtDisplayNameError(validationError);
      return;
    }
    setCourtDisplayNameError('');
    handleCourtChange(editingCourtId, 'displayName', normalizeDisplayName(courtDisplayNameDraft));
    handleCloseCourtDisplayNameModal();
  };

  const handleCategoryChange = (categoryId, field, value, sportType) => {
    setCategories(categories.map(category =>
      category.id === categoryId && category.type === sportType ? { ...category, [field]: value } : category
    ));
  };

  const addCategoryForSport = (sport) => {
    const sportCategories = categories.filter(cat => cat.type === sport && !cat.isFixed && cat.name !== 'Available' && cat.name !== 'Recurring');
    const maxId = sportCategories.reduce((max, cat) => {
      const idNum = parseInt(cat.id, 10);
      return !isNaN(idNum) && idNum > max ? idNum : max;
    }, 0);
    const newNumericId = maxId + 1;

    const newCategory = {
      id: String(newNumericId),
      name: `Category ${newNumericId}`,
      colour: 'slate',
      type: sport,
      isFixed: false,
      feeMultiplier: 1
    };

    setCategories([...categories, newCategory]);
  };

  const removeLastCategoryForSport = (sport) => {
    const sportCategories = categories.filter(cat =>
      cat.type === sport &&
      !cat.isFixed &&
      cat.name !== 'Available' &&
      cat.name !== 'Recurring' &&
      cat.id !== '1'
    );

    if (sportCategories.length > 0) {
      const lastCategory = sportCategories.reduce((maxCat, cat) => {
        const idNum = parseInt(cat.id, 10);
        const maxIdNum = parseInt(maxCat.id, 10);
        return idNum > maxIdNum ? cat : maxCat;
      }, sportCategories[0]);

      setCategories(categories.filter(cat =>
        !(cat.id === lastCategory.id && cat.type === sport)
      ));
    }
  };

  const getNextBookingOptionNumber = (type) => {
    const sportOptions = bookingOptions.filter(option => option.option?.startsWith(`${type}-`));
    const maxId = sportOptions.reduce((max, opt) => {
      const idNum = parseInt(opt.option.split('-')[1], 10);
      return !isNaN(idNum) && idNum > max ? idNum : max;
    }, 0);
    return maxId + 1;
  };

  const handleBookingOptionChange = (optionId, field, value) => {
    setBookingOptions(bookingOptions.map(option =>
      option.option === optionId ? { ...option, [field]: value } : option
    ));
  };

  const addBookingOptionForSport = (sport) => {
    const nextId = getNextBookingOptionNumber(sport);
    const newOption = {
      option: `${sport}-${nextId}`,
      name: `Option ${nextId}`,
    };
    setBookingOptions([...bookingOptions, newOption]);
  };

  const removeLastBookingOptionForSport = (sport) => {
    const sportOptions = bookingOptions.filter(option =>
      option.option?.startsWith(`${sport}-`)
    );

    if (sportOptions.length > 0) {
      const lastOption = sportOptions.reduce((maxOpt, opt) => {
        const idNum = parseInt(opt.option.split('-')[1], 10);
        const maxIdNum = parseInt(maxOpt.option.split('-')[1], 10);
        return idNum > maxIdNum ? opt : maxOpt;
      }, sportOptions[0]);

      setBookingOptions(bookingOptions.filter(option => option.option !== lastOption.option));
    }
  };

  const handleSaveBookingOptions = async () => {
    setIsSavingBookingOptions(true);
    try {
      const optionsPayload = bookingOptions.map((o) => {
        const rest = { ...o };
        delete rest.description;
        return rest;
      });
      await updateBookingOptions({ options: optionsPayload });
      setBookingOptions(optionsPayload);
      const syncedUserTypes = syncUserTypesWithBookingOptions(userTypes, optionsPayload);
      await updateUserTypes(syncedUserTypes);
      setUserTypes(syncedUserTypes);
      setSelectedUserType(prev => {
        if (!prev) return prev;
        const syncedType = syncedUserTypes.find(type => type.id === prev.id);
        return syncedType ? { ...syncedType, sport: prev.sport } : prev;
      });
      setBaseline('bookingOptions', optionsPayload);
      setBaseline('userTypes', syncedUserTypes);
      await refreshAuthSession();
      return true;
    } catch (err) {
      handleError('Failed to save booking options. Please try again.');
      return false;
    } finally {
      setIsSavingBookingOptions(false);
    }
  };

  const handleSaveCourts = async () => {
    setIsSavingCourts(true);
    try {
      const allCourts = courts.map(court => {
        const sport = court.court.split('-')[0];
        const displayName = normalizeDisplayName(court.displayName);
        return {
          ...court,
          displayName,
          sportIndex: availableSports.indexOf(sport),
          switches: Array.isArray(court.switches) ? court.switches.filter(deviceId => deviceId && deviceId.trim() !== '') : []
        };
      });

      const seenDisplayNames = new Set();
      for (const court of allCourts) {
        const normalized = normalizeDisplayName(court.displayName);
        if (!normalized) continue;
        const key = normalized.toLocaleLowerCase();
        if (seenDisplayNames.has(key)) {
          handleError(`Display name "${normalized}" is duplicated. Each display name must be unique.`);
          return false;
        }
        seenDisplayNames.add(key);
      }

      await updateCourts({ courts: allCourts });

      courtDetailsSportsBaselineRef.current = [...availableSports];
      setBaseline('availableSports', availableSports);
      setBaseline('courts', normalizeCourtsForComparison(allCourts, availableSports));
      return true;
    } catch (err) {
      handleError('Failed to save courts. Please try again.');
      return false;
    } finally {
      setIsSavingCourts(false);
    }
  };

  const handleAttemptSaveCourtDetails = () => {
    const baseline = courtDetailsSportsBaselineRef.current;
    const removals = getSportsRequiringRemovalOnCourtSave(baseline, availableSports, courts);
    if (removals.length === 0) {
      handleSaveCourts();
      return;
    }
    courtSaveSnapshotRef.current = {
      availableSports: [...availableSports],
      courts: courts.map((c) => ({ ...c })),
      categories: categories.map((c) => ({ ...c })),
      bookingOptions: bookingOptions.map((o) => ({ ...o })),
      userTypes: userTypes.map((ut) => ({
        ...ut,
        permissions: { ...(ut.permissions || {}) },
      })),
      activeTab,
    };
    setCourtSaveRemovalSports(removals);
    setIsCourtSaveRemovalModalOpen(true);
  };

  const closeCourtSaveRemovalModal = () => {
    const snap = courtSaveSnapshotRef.current;
    if (snap) {
      setAvailableSports(snap.availableSports);
      setCourts(snap.courts);
      setCategories(snap.categories);
      setBookingOptions(snap.bookingOptions);
      setUserTypes(snap.userTypes);
      setActiveTab(snap.activeTab);
    }
    courtSaveSnapshotRef.current = null;
    setCourtSaveRemovalSports([]);
    setIsCourtSaveRemovalModalOpen(false);
  };

  const confirmCourtSaveWithSportRemovals = async () => {
    const list = [...courtSaveRemovalSports];
    setIsSavingCourts(true);
    try {
      for (const sportKey of list) {
        await updateCourts({ sport: sportKey, courts: [] });
        await updateBookingCategories({ sport: sportKey, categories: [] });
        await updateBookingOptions({ sport: sportKey, options: [] });
      }
      let nextUserTypes = userTypes.map((ut) => {
        const perms = { ...(ut.permissions || {}) };
        for (const sk of list) {
          delete perms[sk];
        }
        return { ...ut, permissions: perms };
      });
      await updateUserTypes(nextUserTypes);
      setUserTypes(nextUserTypes);

      const nextSports = availableSports.filter((s) => !list.includes(s));
      const nextCourts = courts.filter((c) => !list.some((sk) => c.court.startsWith(sk)));
      const nextCats = categories.filter((c) => !list.includes(c.type));
      const nextOpts = bookingOptions.filter((o) => !list.some((sk) => o.option?.startsWith(`${sk}-`)));
      setAvailableSports(nextSports);
      setCourts(nextCourts);
      setCategories(nextCats);
      setBookingOptions(nextOpts);
      if (list.includes(activeTab)) {
        setActiveTab(nextSports[0] || '');
      }

      const allCourts = nextCourts.map((court) => {
        const sport = court.court.split('-')[0];
        return {
          ...court,
          sportIndex: nextSports.indexOf(sport),
          switches: Array.isArray(court.switches) ? court.switches.filter((deviceId) => deviceId && deviceId.trim() !== '') : [],
        };
      });
      await updateCourts({ courts: allCourts });

      const normalizedNextCats = nextCats.map(normalizeCategoryFeeMultiplier);
      await updateBookingCategories({ categories: normalizedNextCats });

      courtDetailsSportsBaselineRef.current = [...nextSports];
      setBaseline('availableSports', nextSports);
      setBaseline('courts', normalizeCourtsForComparison(allCourts, nextSports));
      setBaseline('categories', normalizedNextCats);
      setBaseline('bookingOptions', nextOpts);
      setBaseline('userTypes', nextUserTypes);
      courtSaveSnapshotRef.current = null;
      setCourtSaveRemovalSports([]);
      setIsCourtSaveRemovalModalOpen(false);
      await refreshAuthSession();
      return true;
    } catch (err) {
      handleError('Failed to save court details. Please try again.');
      return false;
    } finally {
      setIsSavingCourts(false);
    }
  };

  const handleSaveCategories = async () => {
    setIsSavingCategories(true);
    try {
      const normalizedCategories = categories.map(normalizeCategoryFeeMultiplier);
      await updateBookingCategories({ categories: normalizedCategories });
      setBaseline('categories', normalizedCategories);
      return true;
    } catch (err) {
      handleError('Failed to save categories. Please try again.');
      return false;
    } finally {
      setIsSavingCategories(false);
    }
  };

  const handleClubDetailsChange = (e) => {
    const { name, value } = e.target;

    if (name === 'googleMapEmbedUrl') {
      // Transform the input if it's the googleMapEmbedUrl field
      const transformedValue = extractMapSrc(value);
      setClubDetails(prev => ({ ...prev, [name]: transformedValue }));
    } else {
      setClubDetails(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAccessControlModeChange = (e) => {
    const mode = e.target.value;
    setAccessControlShellyTest({ status: 'idle', message: '' });
    if (mode === 'none') {
      setClubDetails(prev => ({ ...prev, accessControl: null }));
      return;
    }
    if (mode === 'access_code') {
      setClubDetails(prev => {
        const prevAc = prev.accessControl;
        const keep = prevAc?.type === 'access_code' ? prevAc : null;
        return {
          ...prev,
          accessControl: {
            type: 'access_code',
            code: keep?.code ?? '',
            minutesBeforeStart: keep?.minutesBeforeStart ?? '',
          },
        };
      });
      return;
    }
    if (mode === 'shelly') {
      setClubDetails(prev => {
        const prevAc = prev.accessControl;
        const keep = prevAc?.type === 'shelly' ? prevAc : null;
        const deviceTrimmed = String(keep?.deviceId ?? '').trim();
        return {
          ...prev,
          accessControl: {
            type: 'shelly',
            deviceId: keep?.deviceId ?? '',
            channel: deviceTrimmed ? normalizeAccessControlChannel(keep?.channel ?? '0') : '',
            minutesBeforeStart: keep?.minutesBeforeStart ?? '',
          },
        };
      });
    }
  };

  const handleShellyAccessControlDeviceIdChange = (value) => {
    setAccessControlShellyTest({ status: 'idle', message: '' });
    setClubDetails(prev => {
      const ac = prev.accessControl;
      if (!ac || ac.type !== 'shelly') return prev;
      const trimmed = String(value || '').trim();
      if (!trimmed) {
        return { ...prev, accessControl: { ...ac, deviceId: value, channel: '' } };
      }
      const prevCh = String(ac.channel ?? '').trim();
      const channelNext = prevCh === '' ? '0' : ac.channel;
      return {
        ...prev,
        accessControl: {
          ...ac,
          deviceId: value,
          channel: normalizeAccessControlChannel(channelNext),
        },
      };
    });
  };

  const patchAccessControlFields = (updates) => {
    setAccessControlShellyTest({ status: 'idle', message: '' });
    setClubDetails(prev => {
      const ac = prev.accessControl;
      if (!ac || typeof ac !== 'object') return prev;
      return { ...prev, accessControl: { ...ac, ...updates } };
    });
  };

  const handleAccessControlMinutesMouseDown = (e) => {
    const prev = clubDetails.accessControl?.minutesBeforeStart;
    if (!isAccessControlMinutesValueEmpty(prev)) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const SPINNER_RIGHT = 32;
    if (rect.width <= SPINNER_RIGHT || x <= rect.width - SPINNER_RIGHT) return;
    const fracY = (e.clientY - rect.top) / rect.height;
    accessControlMinutesSpinUpPendingRef.current = fracY < 0.5;
  };

  const handleAccessControlMinutesBlur = () => {
    accessControlMinutesSpinUpPendingRef.current = false;
  };

  const handleAccessControlMinutesChange = (e) => {
    const prev = clubDetails.accessControl?.minutesBeforeStart;
    let next = normalizeAccessControlMinutesDigitsInput(e.target.value);
    if (
      accessControlMinutesSpinUpPendingRef.current &&
      isAccessControlMinutesValueEmpty(prev) &&
      next === 1
    ) {
      next = 0;
    }
    accessControlMinutesSpinUpPendingRef.current = false;
    patchAccessControlFields({ minutesBeforeStart: next });
  };

  const handleAccessControlMinutesKeyDown = (e) => {
    handleAccessControlWholeMinutesKeyDown(e);
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      accessControlMinutesSpinUpPendingRef.current = false;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      accessControlMinutesSpinUpPendingRef.current = false;
    }
    const prev = clubDetails.accessControl?.minutesBeforeStart;
    if (e.key === 'ArrowUp' && isAccessControlMinutesValueEmpty(prev)) {
      e.preventDefault();
      accessControlMinutesSpinUpPendingRef.current = false;
      patchAccessControlFields({ minutesBeforeStart: 0 });
    }
  };

  const handleAccessControlShellyTestClick = async () => {
    const ac = clubDetails.accessControl;
    if (!ac || ac.type !== 'shelly') return;
    if (!clubDetails.shellyServerURL || !clubDetails.shellyApiKey) {
      handleError('Configure the Shelly Cloud Server URL and Authorization Key above before testing.');
      return;
    }
    const composed = composeAccessControlShellyDeviceId(ac.deviceId, ac.channel);
    if (!composed) {
      handleError('Enter a Device ID before testing.');
      return;
    }
    setAccessControlShellyTest({ status: 'testing', message: 'Checking device…' });
    try {
      const response = await testShellySwitch({
        serverUrl: clubDetails.shellyServerURL,
        apiKey: clubDetails.shellyApiKey,
        deviceId: composed,
      });
      const deviceStatus = Array.isArray(response?.devices) && response.devices.length === 1
        ? response.devices[0]
        : response;
      const ok = deviceStatus && !deviceStatus.error && deviceStatus.exists && deviceStatus.online;
      setAccessControlShellyTest({
        status: ok ? 'success' : 'error',
        message: formatAccessControlShellyTestMessage(deviceStatus),
      });
    } catch (err) {
      setAccessControlShellyTest({
        status: 'error',
        message: err?.message || 'Unable to verify this switch. Please try again.',
      });
    }
  };

  const handleLogout = () => {
    logoutUser();
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Utility classes
  const inputClass = "mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-sm md:text-base";
  const labelClass = "block text-sm font-medium text-slate-600 md:text-base";
  const buttonClass = "px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors md:text-base";
  const iconButtonClass = "p-2 rounded-full transition-colors";
  const thClass = "px-3 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider md:px-4 md:text-sm";
  const tdClass = "px-3 py-3 align-middle whitespace-nowrap text-sm text-slate-600 md:px-4 md:text-base";
  const textInputInTableCellClass = "w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 md:text-base h-[38px]";
  /** Same base as PermissionsModal number fields (`inputClass`), narrow width for small values. */
  const feeMultiplierInputClass = `${inputClass} !mt-0 h-[38px] w-full max-w-[5.5rem] mx-auto text-center tabular-nums`;
  const disabledInputClass = "w-full text-sm px-3 py-2 bg-slate-100 border border-slate-300 rounded-md shadow-sm cursor-not-allowed md:text-base h-[38px]";
  const timeInputClass = "w-full text-sm px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 md:text-base h-[38px] max-w-[120px] md:max-w-[150px] cursor-pointer";

  /** Shared height for Access Control inputs / Test button (aligned row). */
  const accessControlControlHeightClass = 'h-[42px] box-border';

  const getSportDisplayName = (sport) => {
    return formatSportDisplayName(sport);
  };

  // Add handlers for contacts
  const addContact = (type) => {
    setClubDetails(prev => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        type === 'phone'
          ? { name: '', phone: '' }
          : { name: '', email: '' }
      ]
    }));
  };

  const removeContact = (type) => {
    setClubDetails(prev => {
      const filtered = prev.contacts.filter(c => (type === 'phone' ? c.phone !== undefined : c.email !== undefined));
      if (filtered.length <= 1) return prev; // Don't remove last of type
      // Find the last index of the relevant type
      const lastIdx = [...prev.contacts].reverse().findIndex(c => type === 'phone' ? c.phone !== undefined : c.email !== undefined);
      if (lastIdx === -1) return prev;
      // Calculate the actual index from the end
      const removeIdx = prev.contacts.length - 1 - lastIdx;
      return {
        ...prev,
        contacts: prev.contacts.filter((_, idx) => idx !== removeIdx)
      };
    });
  };

  const handleContactChange = (index, field, value) => {
    setClubDetails(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      )
    }));
    if (field === 'email') {
      validateEmail(value, index);
    }
    console.log('Updated Phone Number:', value);
  };

  /** Valid contacts only; phone entries include `whatsapp: true` only when enabled. */
  const buildContactsForSave = (contacts) =>
    contacts
      .filter((c) => {
        if (c.phone !== undefined) {
          return Boolean(c.name && c.phone);
        }
        if (c.email !== undefined) {
          return Boolean(c.name && c.email);
        }
        return false;
      })
      .map((c) => {
        if (c.phone !== undefined) {
          const phone = c.phone.startsWith('+') ? c.phone : `+${c.phone}`;
          const entry = { name: c.name, phone };
          if (c.whatsapp === true) {
            entry.whatsapp = true;
          }
          return entry;
        }
        return { name: c.name, email: c.email };
      });

  // Email validation
  const validateEmail = (email, index) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setErrors(prev => ({
      ...prev,
      [`email-${index}`]: email && !emailRegex.test(email) ? 'Please enter a valid email address' : ''
    }));
  };

  // User Types handlers
  const handleUserTypeNameChange = (id, newName) => {
    setUserTypes(prev => prev.map(type => 
      type.id === id ? { ...type, name: newName } : type
    ));
  };

  const handlePermissionChange = (userTypeId, category, permission, value) => {
    const targetType = userTypes.find((type) => type.id === userTypeId);
    if (
      targetType
      && wouldGrantPrivilegedClubAccess(category, permission, value)
      && userTypeAllowsOnlineSignup(targetType)
    ) {
      handleError(PRIVILEGED_ACCESS_BLOCKED_BY_ONLINE_SIGNUP_MESSAGE);
      return;
    }

    setUserTypes(prev => prev.map(type => {
      if (type.id === userTypeId) {
        // Special handling for Super User
        if (type.id === 1 && category === 'clubConfiguration') {
          return type; // Prevent any changes to Super User's club configuration
        }

        // If setting Edit to true, ensure View is also true
        if (permission === 'canEdit' && value === true) {
          return {
            ...type,
            permissions: {
              ...type.permissions,
              [category]: {
                ...type.permissions[category],
                canView: true,
                canEdit: true
              }
            }
          };
        }

        // If setting View to false, ensure Edit is also false
        if (permission === 'canView' && value === false) {
          return {
            ...type,
            permissions: {
              ...type.permissions,
              [category]: {
                ...type.permissions[category],
                canView: false,
                canEdit: false
              }
            }
          };
        }

        return {
          ...type,
          permissions: {
            ...type.permissions,
            [category]: {
              ...type.permissions[category],
              [permission]: value
            }
          }
        };
      }
      return type;
    }));
  };

  const addUserType = () => {
    const maxId = userTypes.reduce((max, ut) => Math.max(max, Number(ut.id)), 0);
    const newId = String(maxId + 1);
    
    // Create sport permissions for all available sports
    const sportPermissions = {};
    availableSports.forEach(sport => {
      sportPermissions[sport] = createDefaultSportPermissions();
    });

    const newUserType = {
      id: newId,
      name: `New User Type ${newId}`,
      permissions: {
        clubConfiguration: { canView: false, canEdit: false },
        clubAdmin: { canView: false, canEdit: false },
        ...sportPermissions
      },
      membership: {}
    };
    setUserTypes([...userTypes, normalizeUserType(newUserType)]);
  };

  const removeLastUserType = () => {
    const sorted = [...userTypes].sort((a, b) => Number(a.id) - Number(b.id));
    if (sorted.length > 2) {
      // Only allow removing if last id is not 0 or 1
      const last = sorted[sorted.length - 1];
      if (last.id !== '0' && last.id !== '1') {
        setUserTypes(userTypes.filter(ut => ut.id !== last.id));
      }
    }
  };

  const handleUpdateUserType = (updatedUserType) => {
    const normalized = normalizeUserType(updatedUserType);
    setUserTypes(prev =>
      prev.map(type =>
        type.id === normalized.id
          ? normalized
          : type
      )
    );
    setSelectedUserType(normalized);
  };

  const handleOpenPermissionsModal = (type, sport) => {
    // Always use the latest user type from state
    const latestType = userTypes.find(ut => ut.id === type.id);
    if (!latestType) return;

    let ensuredType = latestType;
    if (!latestType.permissions[sport]) {
      ensuredType = normalizeUserType({
        ...latestType,
        permissions: {
          ...latestType.permissions,
          [sport]: createDefaultSportPermissions()
        }
      });
      setUserTypes(prev =>
        prev.map(t => (t.id === ensuredType.id ? ensuredType : t))
      );
    } else {
      ensuredType = normalizeUserType(latestType);
    }

    setSelectedUserType({ ...ensuredType, sport });
    setIsPermissionsModalOpen(true);
  };

  const handleOpenMembershipModal = (type) => {
    // Always use the latest user type from state
    const latestType = userTypes.find(ut => ut.id === type.id);
    setSelectedUserType({ ...latestType });
    setIsMembershipModalOpen(true);
  };

  const handleClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false);
    setSelectedUserType(null);
    setModalErrors({});
    console.log('[ClubConfig] Permissions Modal closed', {
      isPermissionsModalOpen: false,
      selectedUserType: null
    });
  };

  const handleOpenBookingPermissionsPreview = () => {
    if (!selectedUserType) return;
    const slot = buildPreviewBookingSlotData(selectedUserType.sport, courts);
    if (!slot) {
      setError(`Add a court for ${getSportDisplayName(selectedUserType.sport)} under Court Details before previewing the booking form.`);
      setIsErrorModalOpen(true);
      return;
    }
    setBookingPreviewUserTypeSnapshot({ ...selectedUserType });
    setBookingPreviewSlot(slot);
    setIsBookingPermissionPreviewOpen(true);
  };

  const handleCloseBookingPermissionsPreview = () => {
    setIsBookingPermissionPreviewOpen(false);
    setBookingPreviewSlot(null);
    setBookingPreviewUserTypeSnapshot(null);
  };

  const bookingPermissionPreviewUser = useMemo(() => {
    if (!user || !bookingPreviewUserTypeSnapshot || !isBookingPermissionPreviewOpen) return null;
    const sport = bookingPreviewUserTypeSnapshot.sport;
    return {
      ...user,
      permissions: {
        ...user.permissions,
        [sport]: bookingPreviewUserTypeSnapshot.permissions?.[sport] || {}
      }
    };
  }, [user, bookingPreviewUserTypeSnapshot, isBookingPermissionPreviewOpen]);

  const canPreviewBookingForSelectedSport =
    Boolean(selectedUserType && courts.some(c => c?.court?.startsWith(`${selectedUserType.sport}-`)));

  const handleCloseMembershipModal = () => {
    setIsMembershipModalOpen(false);
    setSelectedUserType(null);
    setModalErrors({});
    console.log('[ClubConfig] Membership Modal closed', {
      isMembershipModalOpen: false,
      selectedUserType: null
    });
  };

  const handleOpenSwitchesModal = (court) => {
    setSelectedCourtForSwitches(court);
    setIsSwitchesModalOpen(true);
  };

  const handleCloseSwitchesModal = () => {
    setIsSwitchesModalOpen(false);
    setSelectedCourtForSwitches(null);
  };

  const handleSaveSwitches = async (deviceIds) => {
    if (!selectedCourtForSwitches) return;
    setCourts(prev =>
      prev.map(court =>
        court.court === selectedCourtForSwitches.court
          ? { ...court, switches: deviceIds }
          : court
      )
    );
  };

  const handleTestShellySwitch = async (deviceId) => {
    if (!clubDetails.shellyServerURL || !clubDetails.shellyApiKey) {
      throw new Error('Please configure the Shelly Cloud Server URL and Authorization Key first.');
    }
    const response = await testShellySwitch({
      serverUrl: clubDetails.shellyServerURL,
      apiKey: clubDetails.shellyApiKey,
      deviceId
    });
    return response;
  };

  // Add validation state
  const [validation, setValidation] = useState({});

  // Add error message state
  const [modalErrors, setModalErrors] = useState({});

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setIsErrorModalOpen(true);
  };
  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setError('');
  };

  const handleTermsToggle = (field, value) => {
    if (field === 'enabled') {
      setClubDetails(prev => ({ ...prev, termsEnabled: value }));
    } else if (field === 'enforceOnRegistration') {
      setClubDetails(prev => ({ ...prev, termsEnforced: value }));
    }
  };

  const handleTermsHtmlSave = (html) => {
    setClubDetails(prev => ({ ...prev, termsHtml: html }));
    setIsTermsEditOpen(false);
  };

  const handleSaveClubTerms = async () => {
    setIsSavingTerms(true);
    try {
      // Store the values we're about to save
      const termsEnabled = clubDetails.termsEnabled || false;
      const termsEnforced = clubDetails.termsEnforced || false;
      const termsHtml = clubDetails.termsHtml || '';
      
      // Save terms fields as part of clubDetails
      await updateClubDetails({
        ...clubDetails,
        termsEnabled,
        termsEnforced,
        termsHtml
      });

      // Keep local state in sync without GET /getClubDetails — that response often differs
      // slightly (contacts shape, etc.) and made Club Details look dirty while terms baseline was updated.
      const nextState = { ...clubDetails, termsEnabled, termsEnforced, termsHtml };
      setClubDetails(nextState);
      commitSavedClubDetailsToState(nextState);
      return true;
    } catch (err) {
      handleError(err.message || 'Failed to save club terms');
      return false;
    } finally {
      setIsSavingTerms(false);
    }
  };



  const handleSaveClubDetails = async () => {
    setIsSavingClubDetails(true);
    try {
      const filteredContacts = buildContactsForSave(clubDetails.contacts);

      const { terms: _terms, ...rest } = clubDetails;
      const detailsToSave = { ...rest, contacts: filteredContacts };

      // Check payment provider validation rules
      const {
        paymentProvider,
        zapperMerchantId,
        zapperSiteId,
        zapperApiKey,
        yocoPublicKey,
        yocoSecretKey,
        ikhokhaAppKeyId,
        ikhokhaApiSecret,
        ikhokhaMode
      } = detailsToSave;
      
      if (paymentProvider === 'zapper') {
        if (!zapperMerchantId || !zapperSiteId || !zapperApiKey) {
          handleError('Merchant ID, Site ID, and Merchant Site API Key are required when using Zapper Payments');
          return;
        }
      } else if (paymentProvider === 'yoco') {
        if (!yocoPublicKey || !yocoSecretKey) {
          handleError('Public Key and Secret Key are required when using Yoco Payments');
          return;
        }
      } else if (paymentProvider === 'ikhokha') {
        if (!ikhokhaAppKeyId || !ikhokhaApiSecret) {
          handleError('Application Key ID and API Secret are required when using iKhokha Payments');
          return;
        }
        detailsToSave.ikhokhaMode = (ikhokhaMode || 'test').toLowerCase() === 'live' ? 'live' : 'test';
      }

      const acMode = getAccessControlSelectMode(detailsToSave.accessControl);
      if (acMode === 'access_code') {
        const ac = detailsToSave.accessControl;
        const code = String(ac.code || '').trim();
        if (!code) {
          handleError('Access code is required when using Access Code control.');
          return;
        }
        const minsRes = parseAccessControlMinutesForSave(ac.minutesBeforeStart);
        if (!minsRes.ok) {
          handleError(minsRes.message);
          return;
        }
        detailsToSave.accessControl = {
          type: 'access_code',
          code,
          minutesBeforeStart: minsRes.value,
        };
      } else if (acMode === 'shelly') {
        const ac = detailsToSave.accessControl;
        const deviceId = String(ac.deviceId || '').trim();
        if (!deviceId) {
          handleError('Shelly Device ID is required when using Shelly Switch control.');
          return;
        }
        const minsRes = parseAccessControlMinutesForSave(ac.minutesBeforeStart);
        if (!minsRes.ok) {
          handleError(minsRes.message);
          return;
        }
        detailsToSave.accessControl = {
          type: 'shelly',
          deviceId,
          channel: normalizeAccessControlChannel(ac.channel),
          minutesBeforeStart: minsRes.value,
        };
      } else {
        detailsToSave.accessControl = null;
      }

      await updateClubDetails(detailsToSave);
      commitSavedClubDetailsToState(detailsToSave);
      return true;
    } catch (err) {
      handleError(err.message);
      return false;
    } finally {
      setIsSavingClubDetails(false);
    }
  };

  // Sort userTypes by numeric id before rendering
  const sortedUserTypes = [...userTypes].sort((a, b) => Number(a.id) - Number(b.id));

  // Save user types
  const handleSaveUserTypes = async () => {
    setIsSavingUserTypes(true);
    try {
      const syncedUserTypes = syncUserTypesWithBookingOptions(userTypes, bookingOptions);
      const conflictingType = syncedUserTypes.find((type) => hasOnlineSignupPrivilegedAccessConflict(type));
      if (conflictingType) {
        handleError(`"${conflictingType.name}" allows online signup and has Club Admin or Configuration access. Resolve this conflict before saving.`);
        return false;
      }
      await updateUserTypes(syncedUserTypes);
      setUserTypes(syncedUserTypes);
      setSelectedUserType(prev => {
        if (!prev) return prev;
        const syncedType = syncedUserTypes.find(type => type.id === prev.id);
        return syncedType ? { ...syncedType, sport: prev.sport } : prev;
      });
      setBaseline('userTypes', syncedUserTypes);
      await refreshAuthSession();
      return true;
    } catch (err) {
      handleError('Failed to save user types. Please try again.');
      return false;
    } finally {
      setIsSavingUserTypes(false);
    }
  };

  // Add helper function to format category label
  const formatCategoryLabel = (category) => {
    if (category.name === 'Available' || category.name === 'Recurring') {
      return category.name;
    }
    return `Category ${category.id}`;
  };

  const isMandatoryCategory = (category) =>
    category?.name === 'Available' || category?.name === 'Recurring';

  const getCategoryFeeMultiplier = (category) => {
    const rawValue = category?.feeMultiplier ?? category?.feeMuliplier ?? 1;
    if (rawValue === '' || rawValue === null || rawValue === undefined) return 1;
    const parsedValue = Number(rawValue);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) return 1;
    return parsedValue;
  };

  const getCategoryFeeMultiplierDisplayValue = (category) => {
    const rawValue = category?.feeMultiplier ?? category?.feeMuliplier;
    if (rawValue === null || rawValue === undefined) return '1';
    if (rawValue === '') return '';
    return String(rawValue);
  };

  const normalizeCategoryFeeMultiplier = (category) => ({
    ...category,
    feeMultiplier: getCategoryFeeMultiplier(category)
  });

  const handleFeeMultiplierInputChange = (categoryId, sportType, raw) => {
    if (raw === '') {
      handleCategoryChange(categoryId, 'feeMultiplier', '', sportType);
      return;
    }
    handleCategoryChange(categoryId, 'feeMultiplier', raw, sportType);
  };

  const handleFeeMultiplierBlur = (category, sportType) => {
    const n = getCategoryFeeMultiplier(category);
    handleCategoryChange(category.id, 'feeMultiplier', n, sportType);
  };

  const formatBookingOptionLabel = (optionId) => {
    if (!optionId) return 'Option';
    const number = optionId.split('-')[1];
    return `Option ${number}`;
  };

  useEffect(() => {
    const updateFloatingSavePosition = () => {
      const tabBarRect = configTabBarRef.current?.getBoundingClientRect();
      if (!tabBarRect) return;
      const nextTop = Math.max(12, Math.round(tabBarRect.bottom + 8));
      setFloatingSaveTop((prev) => (prev === nextTop ? prev : nextTop));
    };

    const rafId = window.requestAnimationFrame(updateFloatingSavePosition);
    window.addEventListener('resize', updateFloatingSavePosition);
    window.addEventListener('scroll', updateFloatingSavePosition, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateFloatingSavePosition);
      window.removeEventListener('scroll', updateFloatingSavePosition, true);
    };
  }, [isDataLoading, configTab, user?.permissions?.clubConfiguration?.canEdit]);

  // Permission checks
  if (!user?.permissions?.clubConfiguration?.canView) {
    return (
      <div>
        <ErrorModal
          isOpen={isErrorModalOpen}
          onClose={closeErrorModal}
          errorMessage={error}
        />
      </div>
    );
  }
  const canEditClubConfig = user?.permissions?.clubConfiguration?.canEdit;
  const isSavingAny =
    isSavingClubDetails ||
    isSavingCourts ||
    isSavingBookingOptions ||
    isSavingCategories ||
    isSavingUserTypes ||
    isSavingEmailTemplates ||
    isSavingTerms;

  // Show loading spinner while data is being fetched
  if (isDataLoading || availableSports.length === 0) {
    return (
      <div className="p-4 bg-slate-100 min-h-screen">
        <div className="shadow-md rounded-lg bg-white p-6 flex items-center justify-center text-slate-600">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading configuration...</span>
          </div>
        </div>
      </div>
    );
  }

  // Ensure we have a valid activeTab
  const currentActiveTab = activeTab && availableSports.includes(activeTab) ? activeTab : availableSports[0];
  const currentTerms = {
    enabled: clubDetails.termsEnabled || false,
    enforceOnRegistration: clubDetails.termsEnforced || false,
    html: clubDetails.termsHtml || ''
  };

  const extractMapSrc = (iframeString) => {
    const srcMatch = iframeString.match(/src="([^"]+)"/);
    return srcMatch ? srcMatch[1] : ''; // Return the src URL or an empty string if not found
  };

  // Add handler for email template changes
  const handleEmailTemplateChange = (id, field, value) => {
    setEmailTemplates(prev => prev.map(template => 
      template.id === id ? { ...template, [field]: value } : template
    ));
  };

  const openPreviewModal = (template) => {
    setSelectedTemplate(template);
    setIsPreviewModalOpen(true);
  };
  
  const closePreviewModal = () => {
    setSelectedTemplate(null);
    setIsPreviewModalOpen(false);
  };

  // Add handler for saving email templates
  const handleSaveEmailTemplates = async () => {
    setIsSavingEmailTemplates(true);
    try {
      const templatesWithTextBody = emailTemplates.map((template) => ({
        ...template,
        textBody: emailTemplateHtmlToText(template.htmlBody),
      }));
      await updateEmailTemplates(templatesWithTextBody);
      setEmailTemplates(templatesWithTextBody);
      setBaseline('emailTemplates', templatesWithTextBody);
      return true;
    } catch (err) {
      handleError('Failed to save email templates. Please try again.');
      return false;
    } finally {
      setIsSavingEmailTemplates(false);
    }
  };

  // Handler for saving changes from the edit modal
  const handleEditModalSave = (updatedTemplate) => {
    handleEmailTemplateChange(updatedTemplate.id, 'subject', updatedTemplate.subject);
    handleEmailTemplateChange(updatedTemplate.id, 'htmlBody', updatedTemplate.htmlBody);
    handleEmailTemplateChange(updatedTemplate.id, 'ccAddresses', updatedTemplate.ccAddresses);
    handleEmailTemplateChange(updatedTemplate.id, 'bccAddresses', updatedTemplate.bccAddresses);
    setActiveEditTemplate(null);
  };



  const CONFIG_TABS = [
    { id: 'club-details',         label: 'Club Details'       },
    { id: 'court-details',        label: 'Court Details'      },
    { id: 'booking-options',      label: 'Booking Options'    },
    { id: 'booking-categories',   label: 'Booking Categories' },
    { id: 'user-types',           label: 'User Types'         },
    { id: 'email-templates',      label: 'Email Templates'    },
    { id: 'club-terms',           label: 'Club Terms'         },
  ];

  const activeConfigTab = CONFIG_TABS.find(t => t.id === configTab) || CONFIG_TABS[0];

  const sportAvailableBorderClass = (sportKey) =>
    TAILWIND_COLORS[categories.find(c => c.type === sportKey && c.name === 'Available')?.colour]?.border || 'border-slate-300';

  const reorderSports = (dragged, targetSport) => {
    if (!dragged || dragged === targetSport) {
      setDraggedSport(null);
      return;
    }
    const currentOrder = [...availableSports];
    const fromIndex = currentOrder.indexOf(dragged);
    const toIndex = currentOrder.indexOf(targetSport);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggedSport(null);
      return;
    }
    currentOrder.splice(fromIndex, 1);
    currentOrder.splice(toIndex, 0, dragged);
    setAvailableSports(currentOrder);
    setCourts(prevCourts =>
      prevCourts.map(court => {
        const courtSport = court.court.split('-')[0];
        const sportIndex = currentOrder.indexOf(courtSport);
        return {
          ...court,
          sportIndex: sportIndex === -1 ? court.sportIndex : sportIndex,
        };
      }),
    );
    setDraggedSport(null);
  };

  const saveAllChanges = async () => {
    if (totalDirtyCount === 0) return;

    if (hasUnsavedChanges.clubDetails()) {
      const saved = await handleSaveClubDetails();
      if (!saved) return;
    }

    if (hasUnsavedChanges.courtDetails()) {
      const removals = getSportsRequiringRemovalOnCourtSave(
        courtDetailsSportsBaselineRef.current,
        availableSports,
        courts
      );
      if (removals.length > 0) {
        setCourtSaveRemovalSports(removals);
        setIsCourtSaveRemovalModalOpen(true);
        return;
      }
      const saved = await handleSaveCourts();
      if (!saved) return;
    }

    if (hasUnsavedChanges.bookingOptions()) {
      const saved = await handleSaveBookingOptions();
      if (!saved) return;
    }

    if (hasUnsavedChanges.bookingCategories()) {
      const saved = await handleSaveCategories();
      if (!saved) return;
    }

    if (hasUnsavedChanges.userTypes()) {
      const saved = await handleSaveUserTypes();
      if (!saved) return;
    }

    if (hasUnsavedChanges.emailTemplates()) {
      const saved = await handleSaveEmailTemplates();
      if (!saved) return;
    }

    if (hasUnsavedChanges.clubTerms() && !hasUnsavedChanges.clubDetails()) {
      await handleSaveClubTerms();
    }
  };

  return (
    <div className="bg-slate-100 min-h-screen">

      {/* ─── Tab bar ─── */}
      <div ref={configTabBarRef} className="sticky top-0 z-40 bg-white shadow-sm">

        {/* Desktop: full-width equal tabs until md breakpoint */}
        <div className="hidden md:flex w-full items-stretch border-b border-slate-200">
          {CONFIG_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setConfigTab(tab.id)}
              className={`flex-1 min-w-0 px-2 sm:px-3 py-3 text-center text-xs sm:text-sm leading-snug border-b-[3px] transition-colors duration-150 focus:outline-none break-words hyphens-auto ${
                configTab === tab.id
                  ? 'font-semibold text-slate-800 border-blue-600'
                  : dirtyTabs[tab.id]
                    ? 'font-medium text-amber-700 border-amber-400 hover:text-amber-800'
                    : 'font-medium text-slate-500 border-transparent hover:text-slate-800'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                {dirtyTabs[tab.id] ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" /> : null}
              </span>
            </button>
          ))}
        </div>

        {/* Mobile: horizontally scrolling pill tabs — hidden on desktop */}
        <div className="flex md:hidden overflow-x-auto px-3 py-2 gap-2" style={{scrollbarWidth:'none'}}
          onScroll={e => e.stopPropagation()}>
          {CONFIG_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setConfigTab(tab.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors duration-150 focus:outline-none ${
                configTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : dirtyTabs[tab.id]
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.label}
                {dirtyTabs[tab.id] ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" /> : null}
              </span>
            </button>
          ))}
        </div>

      </div>

      {canEditClubConfig && (
        <div className="fixed right-4 z-20" style={{ top: floatingSaveTop }}>
          <button
            type="button"
            onClick={saveAllChanges}
            disabled={isSavingAny || totalDirtyCount === 0}
            className={`py-2 px-4 rounded-md text-white font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 ${
              isSavingAny ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
            style={{ minWidth: 220 }}
          >
            {isSavingAny ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : totalDirtyCount > 0 ? `Save ${totalDirtyCount} change${totalDirtyCount === 1 ? '' : 's'}` : 'Save changes'}
          </button>
        </div>
      )}

      <div className="cfg-content mx-auto w-full max-w-7xl p-4 pt-6 sm:p-6">

      {/* ── CLUB DETAILS TAB ── */}
      {configTab === 'club-details' && (<>
      <div className="space-y-5">

        {/* ── Basic Info ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Basic Information</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="clubName" className={labelClass}>Club Name</label>
              <input type="text" name="name" id="clubName" value={clubDetails.name} onChange={handleClubDetailsChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="clubLocation" className={labelClass}>Address</label>
              <input type="text" name="location" id="clubLocation" value={clubDetails.location} onChange={handleClubDetailsChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="googleMapEmbedUrl" className={labelClass}>Google Maps Embed URL</label>
              <input
                type="text"
                name="googleMapEmbedUrl"
                id="googleMapEmbedUrl"
                value={clubDetails.googleMapEmbedUrl || ''}
                onChange={handleClubDetailsChange}
                className={inputClass}
                placeholder="Paste the src part or iframe of a shared Google Maps Embed URL"
              />
            </div>
            <div>
              <label htmlFor="clubWebsite" className={labelClass}>Website</label>
              <input type="url" name="website" id="clubWebsite" value={clubDetails.website} onChange={handleClubDetailsChange} className={inputClass} />
            </div>
          </div>
        </div>

        {/* ── Phone Contacts ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Phone Contacts</h3>
          </div>
          <div className="p-5 space-y-3">
            {clubDetails.contacts
              .map((contact, idx) => ({ contact, idx }))
              .filter(({ contact }) => contact.phone !== undefined)
              .map(({ contact, idx }) => (
                <div key={`phone-${idx}`} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div style={{ height: '40px', display: 'flex', alignItems: 'flex-end' }}>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={e => handleContactChange(idx, 'name', e.target.value)}
                      className={`${inputClass} h-full mt-0 py-0`}
                      placeholder="Label (e.g. Main Office)"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3 min-w-0">
                    <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }} className="flex-1 min-w-0">
                      <PhoneInput
                        country={'za'}
                        value={contact.phone}
                        onChange={value => handleContactChange(idx, 'phone', value)}
                        enableSearch={true}
                        searchStyle={{
                          width: '100%',
                          height: '100%',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem',
                          borderRadius: '0.375rem',
                          border: '1px solid #d1d5db',
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '100%',
                          padding: '0 0.75rem',
                          fontSize: '0.875rem',
                          lineHeight: '1.25rem',
                          borderRadius: '0.375rem',
                          border: '1px solid #d1d5db',
                          backgroundColor: '#fff',
                          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                          transition: 'all 0.15s ease-in-out',
                        }}
                        containerStyle={{
                          width: '100%',
                          height: '40px',
                          minHeight: '40px',
                          marginTop: '0',
                          marginBottom: '0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'flex-start',
                        }}
                        buttonStyle={{
                          background: 'transparent',
                          border: 'none',
                          padding: '0 0 0 6px',
                          height: '90%',
                          position: 'absolute',
                          left: '1px',
                          top: '1px',
                          borderRadius: '0.375rem 0 0 0.375rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        dropdownStyle={{
                          width: '300px',
                          maxHeight: '200px',
                          borderRadius: '0.375rem',
                          border: '1px solid #d1d5db',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          marginTop: '4px',
                        }}
                        inputProps={{
                          placeholder: 'Phone number',
                          style: {
                            height: '100%',
                            width: '100%',
                            outline: 'none',
                          },
                          onFocus: e => {
                            e.target.style.borderColor = '#6366f1';
                            e.target.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.2)';
                          },
                          onBlur: e => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';
                          },
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 shrink-0 pb-1 sm:pb-2" title="Offer a WhatsApp chat link for this number on the contacts page">
                      <FaWhatsapp className="h-5 w-5 text-green-600 shrink-0" aria-hidden />
                      <label className="inline-flex relative items-center cursor-pointer">
                        <span className="sr-only">WhatsApp link for this number</span>
                        <input
                          type="checkbox"
                          checked={contact.whatsapp === true}
                          onChange={e => handleContactChange(idx, 'whatsapp', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            <div className="flex gap-2 pt-1">
              <button onClick={() => addContact('phone')} className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`} title="Add Phone Contact"><PlusIcon className="h-5 w-5" /></button>
              <button onClick={() => removeContact('phone')} className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`} title="Remove Last Phone Contact" disabled={clubDetails.contacts.filter(c => c.phone !== undefined).length <= 1}><MinusIcon className="h-5 w-5" /></button>
            </div>
          </div>
        </div>

        {/* ── Email Contacts ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Email Contacts</h3>
          </div>
          <div className="p-5 space-y-3">
            {clubDetails.contacts
              .map((contact, idx) => ({ contact, idx }))
              .filter(({ contact }) => contact.email !== undefined)
              .map(({ contact, idx }) => (
                <div key={`email-${idx}`} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={contact.name}
                    onChange={e => handleContactChange(idx, 'name', e.target.value)}
                    className={`${inputClass} sm:w-48 flex-shrink-0`}
                    placeholder="Label (e.g. Bookings)"
                  />
                  <div className="flex-1">
                    <input
                      type="email"
                      value={contact.email}
                      onChange={e => handleContactChange(idx, 'email', e.target.value)}
                      onBlur={e => validateEmail(e.target.value, idx)}
                      className={`${inputClass} ${errors[`email-${idx}`] ? 'border-red-500' : ''}`}
                      placeholder="email@example.com"
                    />
                    {errors[`email-${idx}`] && <p className="mt-1 text-sm text-red-500">{errors[`email-${idx}`]}</p>}
                  </div>
                </div>
              ))}
            <div className="flex gap-2 pt-1">
              <button onClick={() => addContact('email')} className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`} title="Add Email Contact"><PlusIcon className="h-5 w-5" /></button>
              <button onClick={() => removeContact('email')} className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`} title="Remove Last Email Contact" disabled={clubDetails.contacts.filter(c => c.email !== undefined).length <= 1}><MinusIcon className="h-5 w-5" /></button>
            </div>
          </div>
        </div>

        {/* ── Social Media ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Social Media</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="clubFacebook" className={labelClass}>Facebook</label>
              <input type="url" name="facebook" id="clubFacebook" value={clubDetails.facebook} onChange={handleClubDetailsChange} className={inputClass} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label htmlFor="clubInstagram" className={labelClass}>Instagram</label>
              <input type="url" name="instagram" id="clubInstagram" value={clubDetails.instagram} onChange={handleClubDetailsChange} className={inputClass} placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label htmlFor="clubX" className={labelClass}>X (Twitter)</label>
              <input type="url" name="xdotcom" id="clubX" value={clubDetails.xdotcom} onChange={handleClubDetailsChange} className={inputClass} placeholder="https://x.com/..." />
            </div>
            <div>
              <label htmlFor="clubYoutube" className={labelClass}>YouTube</label>
              <input type="url" name="youtube" id="youtube" value={clubDetails.youtube} onChange={handleClubDetailsChange} className={inputClass} placeholder="https://youtube.com/..." />
            </div>
          </div>
        </div>

        {/* ── Payment Provider ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Payment Provider</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="paymentProvider" className={labelClass}>Provider</label>
              <select name="paymentProvider" id="paymentProvider" value={clubDetails.paymentProvider || 'none'} onChange={handleClubDetailsChange} className={inputClass}>
                <option value="none">None</option>
                <option value="zapper">Zapper</option>
                <option value="yoco">Yoco</option>
                <option value="ikhokha">iKhokha</option>
              </select>
              <p className="mt-1.5 text-xs text-slate-500">Select the payment provider for processing payments</p>
            </div>

            {/* Zapper */}
            {clubDetails.paymentProvider === 'zapper' && (<>
              <div>
                <label htmlFor="zapperMerchantId" className={labelClass}>Merchant ID</label>
                <input type="text" name="zapperMerchantId" id="zapperMerchantId" value={clubDetails.zapperMerchantId} onChange={handleClubDetailsChange} className={inputClass} placeholder="Enter your Zapper Merchant ID" />
              </div>
              <div>
                <label htmlFor="zapperSiteId" className={labelClass}>Site ID</label>
                <input type="text" name="zapperSiteId" id="zapperSiteId" value={clubDetails.zapperSiteId} onChange={handleClubDetailsChange} className={inputClass} placeholder="Enter your Zapper Site ID" />
              </div>
              <div>
                <label htmlFor="zapperApiKey" className={labelClass}>Merchant Site API Key</label>
                <div className="relative">
                  <input type={showZapperApiKey ? 'text' : 'password'} name="zapperApiKey" id="zapperApiKey" value={clubDetails.zapperApiKey} onChange={handleClubDetailsChange} autoComplete="new-password" className={`${inputClass} pr-10`} placeholder="Enter your Zapper Merchant Site API Key" />
                  <button type="button" onClick={() => setShowZapperApiKey(!showZapperApiKey)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                    {showZapperApiKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500">These IDs are used for processing payments through Zapper. The API Key is required to check payment status when a booking times out.</p>
            </>)}

            {/* Yoco */}
            {clubDetails.paymentProvider === 'yoco' && (<>
              <div>
                <label htmlFor="yocoPublicKey" className={labelClass}>Public Key</label>
                <input type="text" name="yocoPublicKey" id="yocoPublicKey" value={clubDetails.yocoPublicKey} onChange={handleClubDetailsChange} className={inputClass} placeholder="Enter your Yoco Public Key" />
              </div>
              <div>
                <label htmlFor="yocoSecretKey" className={labelClass}>Secret Key</label>
                <div className="relative">
                  <input type={showYocoSecretKey ? 'text' : 'password'} name="yocoSecretKey" id="yocoSecretKey" value={clubDetails.yocoSecretKey} onChange={handleClubDetailsChange} autoComplete="new-password" className={`${inputClass} pr-10`} placeholder="Enter your Yoco Secret Key" />
                  <button type="button" onClick={() => setShowYocoSecretKey(!showYocoSecretKey)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                    {showYocoSecretKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500">These keys are used for processing payments through Yoco. The Secret Key is required to create checkout sessions and verify payment status.</p>
            </>)}

            {/* iKhokha */}
            {clubDetails.paymentProvider === 'ikhokha' && (<>
              <div>
                <label htmlFor="ikhokhaAppKeyId" className={labelClass}>Application Key ID</label>
                <input type="text" name="ikhokhaAppKeyId" id="ikhokhaAppKeyId" value={clubDetails.ikhokhaAppKeyId} onChange={handleClubDetailsChange} className={inputClass} placeholder="Application Key ID" />
              </div>
              <div>
                <label htmlFor="ikhokhaApiSecret" className={labelClass}>API Secret</label>
                <div className="relative">
                  <input type={showIkhokhaApiSecret ? 'text' : 'password'} name="ikhokhaApiSecret" id="ikhokhaApiSecret" value={clubDetails.ikhokhaApiSecret} onChange={handleClubDetailsChange} autoComplete="new-password" className={`${inputClass} pr-10`} placeholder="API Secret" />
                  <button type="button" onClick={() => setShowIkhokhaApiSecret(!showIkhokhaApiSecret)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                    {showIkhokhaApiSecret ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="ikhokhaMode" className={labelClass}>Mode</label>
                <select name="ikhokhaMode" id="ikhokhaMode" value={clubDetails.ikhokhaMode || 'test'} onChange={handleClubDetailsChange} className={inputClass}>
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </div>
              <p className="text-xs text-slate-500">Application Key ID, API Secret, and mode are used to create checkout sessions and verify payments via iKhokha.</p>
            </>)}
          </div>
        </div>

        {/* ── Shelly Cloud API ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Shelly Cloud API</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label htmlFor="shellyServerURL" className={labelClass}>Server URL</label>
              <input type="url" name="shellyServerURL" id="shellyServerURL" value={clubDetails.shellyServerURL} onChange={handleClubDetailsChange} className={inputClass} placeholder="https://shelly-xx-eu.shelly.cloud" />
            </div>
            <div>
              <label htmlFor="shellyApiKey" className={labelClass}>Authorization Cloud Key</label>
              <div className="relative">
                <input type={showShellyApiKey ? 'text' : 'password'} name="shellyApiKey" id="shellyApiKey" value={clubDetails.shellyApiKey} onChange={handleClubDetailsChange} autoComplete="new-password" className={`${inputClass} pr-10`} placeholder="Authorization Cloud Key" />
                <button type="button" onClick={() => setShowShellyApiKey(!showShellyApiKey)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                  {showShellyApiKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Used to control court lights via Shelly Smart Switches. Log in to{' '}
              <a href="https://control.shelly.cloud" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">control.shelly.cloud</a>
              , click <strong>Settings</strong>, expand <strong>Authorization cloud key</strong> and click <strong>Get key</strong>.
            </p>
          </div>
        </div>

        {/* ── Access Control ── */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Access Control</h3>
          </div>
          <div className="p-5 space-y-4">
            <div
              className={`grid w-full items-end gap-x-5 gap-y-4 ${
                getAccessControlSelectMode(clubDetails.accessControl) === 'none'
                  ? 'grid-cols-1'
                  : getAccessControlSelectMode(clubDetails.accessControl) === 'access_code'
                    ? 'grid-cols-1 lg:grid-cols-3'
                    : 'grid-cols-1 lg:grid-cols-4'
              }`}
            >
              <div className="min-w-0">
                <label htmlFor="accessControlMode" className={labelClass}>Method</label>
                <select
                  id="accessControlMode"
                  value={getAccessControlSelectMode(clubDetails.accessControl)}
                  onChange={handleAccessControlModeChange}
                  className={`${inputClass} ${accessControlControlHeightClass} w-full`}
                >
                  <option value="none">None</option>
                  <option value="access_code">Access Code</option>
                  <option value="shelly">Shelly Switch</option>
                </select>
              </div>

              {getAccessControlSelectMode(clubDetails.accessControl) === 'access_code' && (
                <>
                  <div className="min-w-0">
                    <label htmlFor="accessControlCode" className={labelClass}>Access code</label>
                    <div className="relative mt-1">
                      <input
                        id="accessControlCode"
                        type={showAccessControlCode ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={clubDetails.accessControl?.code ?? ''}
                        onChange={(e) => patchAccessControlFields({ code: e.target.value })}
                        className={`block w-full pl-3 pr-10 ${accessControlControlHeightClass} bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono`}
                        placeholder="e.g. 1234#"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAccessControlCode(!showAccessControlCode)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600"
                        aria-label={showAccessControlCode ? 'Hide access code' : 'Show access code'}
                      >
                        {showAccessControlCode ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="accessControlCodeMinutes" className={labelClass}>Show before booking starts (mins)</label>
                    <input
                      id="accessControlCodeMinutes"
                      type="number"
                      min="0"
                      step={1}
                      dir="ltr"
                      inputMode="numeric"
                      onMouseDown={handleAccessControlMinutesMouseDown}
                      onBlur={handleAccessControlMinutesBlur}
                      onKeyDown={handleAccessControlMinutesKeyDown}
                      placeholder="e.g. 15 minutes"
                      value={
                        clubDetails.accessControl?.minutesBeforeStart === '' ||
                        clubDetails.accessControl?.minutesBeforeStart === undefined ||
                        clubDetails.accessControl?.minutesBeforeStart === null ||
                        (typeof clubDetails.accessControl?.minutesBeforeStart === 'number' &&
                          Number.isNaN(clubDetails.accessControl.minutesBeforeStart))
                          ? ''
                          : clubDetails.accessControl.minutesBeforeStart
                      }
                      onChange={handleAccessControlMinutesChange}
                      className={`${inputClass} ${accessControlControlHeightClass} w-full tabular-nums`}
                    />
                  </div>
                </>
              )}

              {getAccessControlSelectMode(clubDetails.accessControl) === 'shelly' && (
                <>
                  <div className="min-w-0">
                    <label htmlFor="accessControlShellyDeviceId" className={labelClass}>Device ID</label>
                    <input
                      id="accessControlShellyDeviceId"
                      type="text"
                      value={clubDetails.accessControl?.deviceId ?? ''}
                      onChange={(e) => handleShellyAccessControlDeviceIdChange(e.target.value)}
                      className={`${inputClass} ${accessControlControlHeightClass} w-full font-mono text-sm`}
                      placeholder="Shelly Switch Device ID"
                    />
                  </div>
                  <div className="flex min-w-0 w-full items-end gap-2 sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <label htmlFor="accessControlShellyChannel" className={labelClass}>Channel</label>
                      <input
                        id="accessControlShellyChannel"
                        type="text"
                        inputMode="numeric"
                        value={
                          String(clubDetails.accessControl?.deviceId || '').trim()
                            ? normalizeAccessControlChannel(clubDetails.accessControl?.channel ?? '0')
                            : ''
                        }
                        onChange={(e) => patchAccessControlFields({ channel: normalizeAccessControlChannel(e.target.value) })}
                        className={`mt-1 block w-full px-3 ${accessControlControlHeightClass} bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm tabular-nums text-center disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed`}
                        placeholder="Channel"
                        disabled={!String(clubDetails.accessControl?.deviceId || '').trim()}
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-end">
                      <span className={`${labelClass} invisible select-none pointer-events-none leading-none`} aria-hidden="true">
                        Test
                      </span>
                      <button
                        type="button"
                        onClick={handleAccessControlShellyTestClick}
                        disabled={accessControlShellyTest.status === 'testing'}
                        title={accessControlShellyTest.status === 'testing' ? 'Testing' : 'Test switch'}
                        className={`mt-1 inline-flex w-full items-center justify-center ${accessControlControlHeightClass} px-2 text-sm font-semibold rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 border ${
                          accessControlShellyTest.status === 'testing'
                            ? 'border-blue-300 bg-blue-200 text-blue-700 cursor-not-allowed'
                            : 'border-blue-300 bg-blue-100 text-blue-700 hover:bg-blue-200 focus:ring-blue-400'
                        }`}
                      >
                        {accessControlShellyTest.status === 'testing' ? '…' : 'Test'}
                      </button>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="accessControlShellyMinutes" className={labelClass}>Show before booking starts (mins)</label>
                    <input
                      id="accessControlShellyMinutes"
                      type="number"
                      min="0"
                      step={1}
                      inputMode="numeric"
                      onMouseDown={handleAccessControlMinutesMouseDown}
                      onBlur={handleAccessControlMinutesBlur}
                      onKeyDown={handleAccessControlMinutesKeyDown}
                      placeholder="e.g. 15 minutes"
                      value={
                        clubDetails.accessControl?.minutesBeforeStart === '' ||
                        clubDetails.accessControl?.minutesBeforeStart === undefined ||
                        clubDetails.accessControl?.minutesBeforeStart === null ||
                        (typeof clubDetails.accessControl?.minutesBeforeStart === 'number' &&
                          Number.isNaN(clubDetails.accessControl.minutesBeforeStart))
                          ? ''
                          : clubDetails.accessControl.minutesBeforeStart
                      }
                      onChange={handleAccessControlMinutesChange}
                      className={`${inputClass} ${accessControlControlHeightClass} w-full tabular-nums`}
                    />
                  </div>
                </>
              )}
            </div>

            <p className="text-xs text-slate-500">
              {getAccessControlMethodDescription(clubDetails.accessControl)}
            </p>

            {getAccessControlSelectMode(clubDetails.accessControl) === 'shelly' && accessControlShellyTest.message ? (
              <div
                className={`rounded-md border p-3 text-sm ${
                  accessControlShellyTest.status === 'success'
                    ? 'bg-green-50 text-green-800 border-green-200'
                    : accessControlShellyTest.status === 'error'
                      ? 'bg-red-50 text-red-800 border-red-200'
                      : 'bg-blue-50 text-blue-800 border-blue-200'
                }`}
              >
                {accessControlShellyTest.message}
              </div>
            ) : null}
          </div>
        </div>

      </div>
      </>)}

      {/* ── COURT DETAILS TAB ── */}
      {configTab === 'court-details' && (
      <>
      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Sports</h3>
          </div>
          <div className="p-5 space-y-3">
            <ul className="space-y-2">
              {availableSports.map((sport) => (
                <li
                  key={sport}
                  draggable
                  onDragStart={() => setDraggedSport(sport)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => reorderSports(draggedSport, sport)}
                  onDragEnd={() => setDraggedSport(null)}
                  className={`flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700 cursor-move ${draggedSport === sport ? 'opacity-60 ring-2 ring-blue-300' : ''}`}
                >
                  <span className="min-w-0 truncate">{getSportDisplayName(sport)}</span>
                  <span className="flex-shrink-0 text-xs font-normal text-slate-400 hidden sm:inline">Drag to reorder</span>
                </li>
              ))}
            </ul>
            {isAddingSport && (
              <div>
                <label htmlFor="newSportNameInput" className={labelClass}>New sport name</label>
                <input
                  id="newSportNameInput"
                  type="text"
                  value={newSportName}
                  onChange={(e) => setNewSportName(sanitizeSportNameForTyping(e.target.value))}
                  onKeyDown={(e) => e.key === 'Enter' && addNewSport()}
                  onBlur={addNewSport}
                  className={inputClass}
                  placeholder="e.g. Padel"
                  autoFocus
                />
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setIsAddingSport(true)}
                className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`}
                title="Add sport"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={stageRemoveLastSport}
                className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`}
                title="Remove last sport in list"
                disabled={availableSports.length <= 1}
              >
                <MinusIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500">Drag a sport onto another to reorder.</p>
          </div>
        </div>

        {availableSports.map((sport) => (
          <div
            key={sport}
            className={`bg-white rounded-xl shadow-sm overflow-hidden border-2 ${sportAvailableBorderClass(sport)}`}
          >
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                {getSportDisplayName(sport)} Courts
              </h3>
            </div>
            <div className="p-5">
              <SportCourtsEditor
                sport={sport}
                courts={courts}
                thClass={thClass}
                tdClass={tdClass}
                timeInputClass={timeInputClass}
                getCourtDisplayLabel={getCourtDisplayLabel}
                handleCourtChange={handleCourtChange}
                onEditCourtDisplayName={handleOpenCourtDisplayNameModal}
                handleOpenSwitchesModal={handleOpenSwitchesModal}
                addCourt={addCourt}
                removeLastCourt={removeLastCourt}
                iconButtonClass={iconButtonClass}
                getSportDisplayName={getSportDisplayName}
                canEditClubConfig={canEditClubConfig}
              />
            </div>
          </div>
        ))}

      </div>
      </>
      )}


      {/* ── BOOKING OPTIONS TAB ── */}
      {configTab === 'booking-options' && (
      <>
      <div className="space-y-5">
        {availableSports.map((sport) => (
          <div
            key={sport}
            className={`bg-white rounded-xl shadow-sm overflow-hidden border-2 ${sportAvailableBorderClass(sport)}`}
          >
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                {getSportDisplayName(sport)} Options
              </h3>
            </div>
            <div className="p-5">
              {bookingOptions.filter((option) => option.option?.startsWith(`${sport}-`)).length === 0 ? (
                <div className="text-center py-8 text-slate-600">
                  Add options for {getSportDisplayName(sport)}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    <div className="grid grid-cols-2 bg-slate-50">
                      <div className={`${thClass} text-left pl-6`}>OPTION</div>
                      <div className={`${thClass} text-center`}>NAME</div>
                    </div>
                    <div className="bg-white divide-y divide-slate-200">
                      {bookingOptions
                        .filter((option) => option.option?.startsWith(`${sport}-`))
                        .map((option) => (
                          <div key={option.option} className="grid grid-cols-2 items-center">
                            <div className={`${tdClass} text-left pl-6 font-medium text-slate-600`}>
                              {formatBookingOptionLabel(option.option)}
                            </div>
                            <div className={`${tdClass} text-center`}>
                              <input
                                type="text"
                                value={option.name || ''}
                                onChange={(e) => handleBookingOptionChange(option.option, 'name', e.target.value)}
                                className={textInputInTableCellClass}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-6 flex justify-start">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => addBookingOptionForSport(sport)}
                    className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`}
                    title="Add Option"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeLastBookingOptionForSport(sport)}
                    className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`}
                    title="Remove Last Option"
                    disabled={bookingOptions.filter((option) => option.option?.startsWith(`${sport}-`)).length === 0}
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

      </div>
      </>
      )}

      {/* ── BOOKING CATEGORIES TAB ── */}
      {configTab === 'booking-categories' && (
      <>
      <div className="space-y-5">
        {availableSports.map((sport) => {
          const sportCategories = categories
            .filter((category) => category.type === sport)
            .sort((a, b) => {
              if (a.name === 'Available') return -1;
              if (b.name === 'Available') return 1;
              if (a.name === 'Recurring') return -1;
              if (b.name === 'Recurring') return 1;
              return parseInt(a.id, 10) - parseInt(b.id, 10);
            });
          return (
          <div
            key={sport}
            className={`bg-white rounded-xl shadow-sm overflow-visible border-2 ${sportAvailableBorderClass(sport)}`}
          >
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                {getSportDisplayName(sport)} Categories
              </h3>
            </div>
            <div className="p-5">

              {/* ── Mobile: one card per category ── */}
              <div className="md:hidden space-y-3">
                {sportCategories.map((category) => (
                  <div key={`mob-${sport}-${category.id}`} className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{formatCategoryLabel(category)}</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${TAILWIND_COLORS[category.colour]?.bg} ${TAILWIND_COLORS[category.colour]?.text} ${TAILWIND_COLORS[category.colour]?.border}`}>
                        {category.name}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                        <input
                          type="text"
                          value={category.name}
                          onChange={(e) => handleCategoryChange(category.id, 'name', e.target.value, sport)}
                          disabled={isMandatoryCategory(category)}
                          className={`w-full text-sm px-3 py-2 rounded-md border shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${isMandatoryCategory(category) ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-300'}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Fee Multiplier</label>
                        {isMandatoryCategory(category) ? (
                          <span className="block text-sm text-slate-400 pt-2">N/A</span>
                        ) : (
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            autoComplete="off"
                            value={getCategoryFeeMultiplierDisplayValue(category)}
                            onChange={(e) => handleFeeMultiplierInputChange(category.id, sport, e.target.value)}
                            onBlur={() => handleFeeMultiplierBlur(category, sport)}
                            className={feeMultiplierInputClass}
                          />
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Colour</label>
                      <ColorPicker
                        value={TAILWIND_COLORS[category.colour]}
                        onChange={(colorValue) => handleCategoryChange(category.id, 'colour', colorValue.colorName, sport)}
                        isOpen={openColorPicker === `${sport}-${category.id}`}
                        onToggle={() => setOpenColorPicker(openColorPicker === `${sport}-${category.id}` ? null : `${sport}-${category.id}`)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Desktop: table ── */}
              {/* No overflow-x-auto here: it creates a scroll container that clips the colour picker vertically. */}
              <div className="hidden md:block overflow-visible">
                <div className="min-w-full">
                  <div className="grid grid-cols-5 bg-slate-50">
                    <div className={`${thClass} text-left pl-6`}>LABEL</div>
                    <div className={`${thClass} text-center`}>NAME</div>
                    <div className={`${thClass} text-center`}>COLOUR</div>
                    <div className={`${thClass} text-center`}>TAG PREVIEW</div>
                    <div className={`${thClass} text-center`}>FEE MULTIPLIER</div>
                  </div>
                  <div className="bg-white divide-y divide-slate-200">
                    {sportCategories.map((category) => (
                      <div key={`${sport}-${category.id}`} className="grid grid-cols-5 items-center">
                        <div className={`${tdClass} text-left pl-6 font-medium text-slate-600`}>
                          {formatCategoryLabel(category)}
                        </div>
                        <div className={`${tdClass} text-center`}>
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => handleCategoryChange(category.id, 'name', e.target.value, sport)}
                            disabled={isMandatoryCategory(category)}
                            className={isMandatoryCategory(category) ? disabledInputClass : textInputInTableCellClass}
                          />
                        </div>
                        <div className={`${tdClass} text-center`}>
                          <div className="flex justify-center">
                            <div className="w-28 md:w-32">
                              <ColorPicker
                                value={TAILWIND_COLORS[category.colour]}
                                onChange={(colorValue) => handleCategoryChange(category.id, 'colour', colorValue.colorName, sport)}
                                isOpen={openColorPicker === `${sport}-${category.id}`}
                                onToggle={() => setOpenColorPicker(openColorPicker === `${sport}-${category.id}` ? null : `${sport}-${category.id}`)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className={`${tdClass} text-center`}>
                          <div className="flex justify-center">
                            <span className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-md border w-28 overflow-hidden text-ellipsis whitespace-nowrap ${TAILWIND_COLORS[category.colour]?.bg} ${TAILWIND_COLORS[category.colour]?.text} ${TAILWIND_COLORS[category.colour]?.border}`}>
                              {category.name}
                            </span>
                          </div>
                        </div>
                        <div className={`${tdClass} text-center`}>
                          {isMandatoryCategory(category) ? (
                            <span className="text-xs text-slate-400">N/A</span>
                          ) : (
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              autoComplete="off"
                              value={getCategoryFeeMultiplierDisplayValue(category)}
                              onChange={(e) => handleFeeMultiplierInputChange(category.id, sport, e.target.value)}
                              onBlur={() => handleFeeMultiplierBlur(category, sport)}
                              className={feeMultiplierInputClass}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => addCategoryForSport(sport)}
                  className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`}
                  title="Add Category"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeLastCategoryForSport(sport)}
                  className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`}
                  title="Remove Last Category"
                  disabled={categories.filter((cat) => cat.type === sport && cat.name !== 'Available' && cat.name !== 'Recurring' && cat.id !== '1').length <= 0}
                >
                  <MinusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          );
        })}

      </div>
      </>
      )}

      {/* ── USER TYPES TAB ── */}
      {configTab === 'user-types' && (
      <>
      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">User Types</h3>
          </div>
          <div className="p-5">

            {/* ── Mobile: one card per user type ── */}
            <div className="md:hidden space-y-4">
              {sortedUserTypes.map((type) => (
                <div key={type.id} className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-4">
                  {/* Name row */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-400 w-5 flex-shrink-0">#{type.id}</span>
                    <input
                      type="text"
                      value={type.name}
                      onChange={(e) => handleUserTypeNameChange(type.id, e.target.value)}
                      className={`flex-1 text-sm px-3 py-2 rounded-md border shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${type.id === '0' || type.id === '1' ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-300'}`}
                      disabled={type.id === '0' || type.id === '1'}
                    />
                  </div>
                  {/* Permissions grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Club Config */}
                    <div className="bg-white rounded-lg border border-slate-200 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Club Config</p>
                      <label className="flex items-center gap-2 mb-1.5">
                        <input type="checkbox" checked={type.permissions.clubConfiguration.canView} onChange={(e) => handlePermissionChange(type.id, 'clubConfiguration', 'canView', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.id === '1' || type.permissions.clubConfiguration.canEdit || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubConfiguration')} />
                        <span className="text-xs text-slate-600">View</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={type.permissions.clubConfiguration.canEdit} onChange={(e) => handlePermissionChange(type.id, 'clubConfiguration', 'canEdit', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.id === '1' || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubConfiguration')} />
                        <span className="text-xs text-slate-600">Edit</span>
                      </label>
                    </div>
                    {/* Club Admin */}
                    <div className="bg-white rounded-lg border border-slate-200 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Club Admin</p>
                      <label className="flex items-center gap-2 mb-1.5">
                        <input type="checkbox" checked={type.permissions.clubAdmin.canView} onChange={(e) => handlePermissionChange(type.id, 'clubAdmin', 'canView', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.permissions.clubAdmin.canEdit || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubAdmin')} />
                        <span className="text-xs text-slate-600">View</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={type.permissions.clubAdmin.canEdit} onChange={(e) => handlePermissionChange(type.id, 'clubAdmin', 'canEdit', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubAdmin')} />
                        <span className="text-xs text-slate-600">Edit</span>
                      </label>
                    </div>
                    {/* Membership */}
                    <div className="bg-white rounded-lg border border-slate-200 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Membership</p>
                      <button
                        onClick={() => handleOpenMembershipModal(type)}
                        disabled={type.id === '0'}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded text-xs font-medium focus:outline-none ${type.id === '0' ? 'border-slate-200 text-slate-400 bg-slate-100 cursor-not-allowed' : 'border-slate-300 text-slate-600 bg-white hover:bg-slate-50'}`}
                        title="Configure Membership"
                      >
                        <UserIcon className="h-3.5 w-3.5 shrink-0" />
                        Configure
                      </button>
                    </div>
                    {/* Sport permissions */}
                    {availableSports.map((sport) => (
                      <div key={sport} className="bg-white rounded-lg border border-slate-200 p-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{getSportDisplayName(sport)}</p>
                        <button
                          onClick={() => handleOpenPermissionsModal(type, sport)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 rounded text-xs font-medium text-slate-600 bg-white hover:bg-slate-50 focus:outline-none"
                          title={`Configure ${getSportDisplayName(sport)} Permissions`}
                        >
                          <Cog6ToothIcon className="h-3.5 w-3.5 shrink-0" />
                          Configure
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop: scrollable table ── */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-full divide-y divide-slate-200">
                <div className={`grid items-center bg-slate-50 py-2`} style={{ gridTemplateColumns: `60px minmax(150px,1fr) minmax(120px,1fr) minmax(120px,1fr) minmax(120px,1fr) ${availableSports.map(() => 'minmax(120px,1fr)').join(' ')}` }}>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase">ID</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase">Name</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Club Config</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Club Admin</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Membership</div>
                  {availableSports.map((sport) => (
                    <div key={sport} className="px-3 text-xs font-medium text-slate-600 uppercase text-center">{getSportDisplayName(sport)}</div>
                  ))}
                </div>
                {sortedUserTypes.map((type) => (
                  <div key={type.id} className={`grid items-center py-2`} style={{ gridTemplateColumns: `60px minmax(150px,1fr) minmax(120px,1fr) minmax(120px,1fr) minmax(120px,1fr) ${availableSports.map(() => 'minmax(120px,1fr)').join(' ')}` }}>
                    <div className="px-3 text-sm font-medium text-slate-600">{type.id}</div>
                    <div className="px-3">
                      <input type="text" value={type.name} onChange={(e) => handleUserTypeNameChange(type.id, e.target.value)} className={type.id === '0' || type.id === '1' ? disabledInputClass : textInputInTableCellClass} disabled={type.id === '0' || type.id === '1'} />
                    </div>
                    <div className="px-3 flex flex-col items-center">
                      <label className="flex items-center justify-center w-full">
                        <input type="checkbox" checked={type.permissions.clubConfiguration.canView} onChange={(e) => handlePermissionChange(type.id, 'clubConfiguration', 'canView', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.id === '1' || type.permissions.clubConfiguration.canEdit || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubConfiguration')} />
                        <span className="ml-1.5 text-xs text-slate-600 min-w-[2rem]">View</span>
                      </label>
                      <label className="flex items-center justify-center w-full mt-1">
                        <input type="checkbox" checked={type.permissions.clubConfiguration.canEdit} onChange={(e) => handlePermissionChange(type.id, 'clubConfiguration', 'canEdit', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.id === '1' || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubConfiguration')} />
                        <span className="ml-1.5 text-xs text-slate-600 min-w-[2rem]">Edit</span>
                      </label>
                    </div>
                    <div className="px-3 flex flex-col items-center">
                      <label className="flex items-center justify-center w-full">
                        <input type="checkbox" checked={type.permissions.clubAdmin.canView} onChange={(e) => handlePermissionChange(type.id, 'clubAdmin', 'canView', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || type.permissions.clubAdmin.canEdit || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubAdmin')} />
                        <span className="ml-1.5 text-xs text-slate-600 min-w-[2rem]">View</span>
                      </label>
                      <label className="flex items-center justify-center w-full mt-1">
                        <input type="checkbox" checked={type.permissions.clubAdmin.canEdit} onChange={(e) => handlePermissionChange(type.id, 'clubAdmin', 'canEdit', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" disabled={type.id === '0' || isPrivilegedPermissionBlockedByOnlineSignup(type, 'clubAdmin')} />
                        <span className="ml-1.5 text-xs text-slate-600 min-w-[2rem]">Edit</span>
                      </label>
                    </div>
                    <div className="px-3 flex justify-center">
                      <button onClick={() => handleOpenMembershipModal(type)} className={`inline-flex items-center p-1.5 border shadow-sm text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${type.id === '0' ? 'border-slate-200 text-slate-400 bg-slate-100 cursor-not-allowed' : 'border-slate-300 text-slate-600 bg-white hover:bg-slate-50'}`} title="Configure Membership Settings" disabled={type.id === '0'}>
                        <UserIcon className="h-4 w-4 shrink-0" />
                      </button>
                    </div>
                    {availableSports.map((sport) => (
                      <div key={sport} className="px-3 flex justify-center">
                        <button onClick={() => handleOpenPermissionsModal(type, sport)} className="inline-flex items-center p-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title={`Configure ${getSportDisplayName(sport)} Booking Permissions`}>
                          <Cog6ToothIcon className="h-4 w-4 shrink-0" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button type="button" onClick={addUserType} className={`${iconButtonClass} text-green-600 hover:text-green-700 hover:bg-green-50`} title="Add User Type"><PlusIcon className="h-5 w-5" /></button>
              <button type="button" onClick={removeLastUserType} className={`${iconButtonClass} text-red-600 hover:text-red-700 hover:bg-red-50`} title="Remove Last User Type" disabled={sortedUserTypes.length <= 2}><MinusIcon className="h-5 w-5" /></button>
            </div>
          </div>
        </div>

      </div>
      </>
      )}

      {/* ── EMAIL TEMPLATES TAB ── */}
      {configTab === 'email-templates' && (
      <>
      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Email Templates</h3>
          </div>
          <div className="p-5">

            {/* ── Mobile: one card per template ── */}
            <div className="md:hidden space-y-3">
              {emailTemplates.map((template) => (
                <div key={template.id} className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-500 truncate">{template.id}</p>
                      <p className="text-sm text-slate-700 mt-0.5">{template.description}</p>
                    </div>
                    <label className="inline-flex relative items-center cursor-pointer flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={template.enabled !== false}
                        onChange={e => handleEmailTemplateChange(template.id, 'enabled', e.target.checked)}
                        className="sr-only peer"
                        disabled={template.id === 'EMAIL_VERIFICATION' || template.id === 'RESET_PASSWORD'}
                      />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-200">
                    <button
                      onClick={() => openPreviewModal(template)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-slate-300 text-xs font-medium rounded-lg text-slate-600 bg-white hover:bg-slate-50"
                      title="Preview"
                    >
                      <EyeIcon className="h-3.5 w-3.5" /> Preview
                    </button>
                    <button
                      onClick={() => setActiveEditTemplate(template)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-slate-300 text-xs font-medium rounded-lg text-slate-600 bg-white hover:bg-slate-50"
                      title="Edit"
                    >
                      <PencilIcon className="h-3.5 w-3.5" /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop: table ── */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-full divide-y divide-slate-200">
                <div className="grid grid-cols-[200px_minmax(300px,1fr)_200px_120px_120px] items-center bg-slate-50 py-2">
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase">ID</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase">Description</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Enabled</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Preview</div>
                  <div className="px-3 text-xs font-medium text-slate-600 uppercase text-center">Edit</div>
                </div>
                {emailTemplates.map((template) => (
                  <div key={template.id} className="grid grid-cols-[200px_minmax(300px,1fr)_200px_120px_120px] items-center py-2">
                    <div className="px-3 text-sm font-medium text-slate-600">{template.id}</div>
                    <div className="px-3 text-sm text-slate-600">{template.description}</div>
                    <div className="px-3 flex justify-center">
                      <label className="inline-flex relative items-center cursor-pointer">
                        <input type="checkbox" checked={template.enabled !== false} onChange={e => handleEmailTemplateChange(template.id, 'enabled', e.target.checked)} className="sr-only peer" disabled={template.id === 'EMAIL_VERIFICATION' || template.id === 'RESET_PASSWORD'} />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="px-3 flex justify-center">
                      <button onClick={() => openPreviewModal(template)} className="inline-flex items-center p-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Preview Email Template">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-3 flex justify-center">
                      <button onClick={() => setActiveEditTemplate(template)} className="inline-flex items-center p-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Edit Email Template">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
      </>
      )}

      {/* ── CLUB TERMS TAB ── */}
      {configTab === 'club-terms' && (
      <>
      <div className="space-y-5">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Club Terms</h3>
          </div>
          <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Define custom club terms, waivers, indemnities, court rules, refund/payment policies, or any other important information. If enabled, users can view them at any time from the menu item Club Terms. Enforcing during registration will require new users to accept before completing signup.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-600">Enable Club Terms</p>
                <p className="text-xs text-slate-500">Show a Club Terms menu item in the sidebar.</p>
              </div>
              <label className="inline-flex relative items-center cursor-pointer ml-3 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={currentTerms.enabled}
                  onChange={(e) => handleTermsToggle('enabled', e.target.checked)}
                  className="sr-only peer"
                  disabled={!canEditClubConfig}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="text-sm font-medium text-slate-600">Enforce During Registration</p>
                <p className="text-xs text-slate-500">Require new users to accept before creating an account.</p>
              </div>
              <label className="inline-flex relative items-center cursor-pointer ml-3 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={currentTerms.enforceOnRegistration}
                  onChange={(e) => handleTermsToggle('enforceOnRegistration', e.target.checked)}
                  className="sr-only peer"
                  disabled={!canEditClubConfig}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Preview and Edit — always inside the card, no wrapping issue */}
          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <button
              onClick={() => setIsTermsPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              type="button"
            >
              <EyeIcon className="h-4 w-4" />
              Preview
            </button>
            {canEditClubConfig && (
              <button
                onClick={() => setIsTermsEditOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                type="button"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Terms
              </button>
            )}
          </div>
          </div>
        </div>

      </div>
      </>
      )}

      </div>{/* end cfg-content */}

      {/* ── GLOBAL MODALS (always rendered, outside tab panels) ── */}
      <ClubTermsModal
        isOpen={isTermsPreviewOpen}
        onClose={() => setIsTermsPreviewOpen(false)}
        htmlContent={clubDetails.termsHtml || ''}
      />

      <ClubTermsEditModal
        isOpen={isTermsEditOpen}
        onClose={() => setIsTermsEditOpen(false)}
        htmlContent={currentTerms.html}
        onSave={handleTermsHtmlSave}
      />

      <EmailTemplateEditModal
        isOpen={!!activeEditTemplate}
        onClose={() => setActiveEditTemplate(null)}
        emailTemplate={activeEditTemplate}
        onSave={handleEditModalSave}
      />

      <EmailTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        emailTemplate={selectedTemplate}
      />
      <PermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={handleClosePermissionsModal}
        selectedUserType={selectedUserType}
        onUpdateUserType={handleUpdateUserType}
        inputClass={inputClass}
        categories={categories}
        bookingOptions={bookingOptions}
        onPreviewBooking={handleOpenBookingPermissionsPreview}
        canPreviewBooking={canPreviewBookingForSelectedSport}
      />

      {isBookingPermissionPreviewOpen && bookingPreviewSlot && bookingPermissionPreviewUser && (
        <BookingModal
          isOpen
          previewMode
          previewUser={bookingPermissionPreviewUser}
          previewUserTypeName={bookingPreviewUserTypeSnapshot?.name ?? ''}
          bookingCategoriesOverride={categories}
          bookingOptionsOverride={bookingOptions}
          onClose={handleCloseBookingPermissionsPreview}
          onSubmit={() => {}}
          slotData={bookingPreviewSlot}
          isEditing={false}
          courts={courts}
          uniqueSportsCount={new Set((courts || []).map(c => c.court.split('-')[0])).size}
          existingBookings={[]}
        />
      )}

      <MembershipModal
        isOpen={isMembershipModalOpen}
        onClose={handleCloseMembershipModal}
        selectedUserType={selectedUserType}
        onUpdateUserType={handleUpdateUserType}
        inputClass={inputClass}
        accessControlType={getAccessControlSelectMode(clubDetails.accessControl)}
        courts={courts}
        categories={categories}
      />

      <SwitchesModal
        isOpen={isSwitchesModalOpen}
        onClose={handleCloseSwitchesModal}
        court={selectedCourtForSwitches}
        onSave={handleSaveSwitches}
        onTestSwitch={handleTestShellySwitch}
        shellyServerURL={clubDetails.shellyServerURL}
        shellyApiKey={clubDetails.shellyApiKey}
      />

      {isCourtDisplayNameModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={handleCloseCourtDisplayNameModal} />
          <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-slate-800">Edit Display Name</h3>
            <p className="mt-1 text-sm text-slate-600">
              Set how this resource label appears to users. Leave blank to use the default Court label.
            </p>
            <div className="mt-4">
              <label className={labelClass} htmlFor="courtDisplayNameInput">Display Name</label>
              <input
                id="courtDisplayNameInput"
                type="text"
                value={courtDisplayNameDraft}
                onChange={(e) => {
                  setCourtDisplayNameDraft(e.target.value);
                  if (courtDisplayNameError) {
                    setCourtDisplayNameError('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveCourtDisplayName();
                  if (e.key === 'Escape') handleCloseCourtDisplayNameModal();
                }}
                className={`${inputClass} ${courtDisplayNameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="e.g. Table 1 or Studio"
                autoFocus
              />
              {courtDisplayNameError && (
                <p className="mt-2 text-sm text-red-600">{courtDisplayNameError}</p>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseCourtDisplayNameModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCourtDisplayName}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        errorMessage={error}
      />

      <ConfirmationModal
        isOpen={isCourtSaveRemovalModalOpen}
        onClose={closeCourtSaveRemovalModal}
        onConfirm={confirmCourtSaveWithSportRemovals}
        title="Remove sports"
        message={
          courtSaveRemovalSports.length === 0
            ? ''
            : courtSaveRemovalSports.length === 1
              ? `Saving will permanently remove ${getSportDisplayName(courtSaveRemovalSports[0])} and delete all of its courts, booking categories, booking options, and user permissions for that sport.`
              : `Saving will permanently remove these sports: ${courtSaveRemovalSports.map((s) => getSportDisplayName(s)).join(', ')}. For each sport, all courts, booking categories, booking options, and user permissions will be deleted.`
        }
        confirmText="Save and remove"
        cancelText="Cancel"
        isDestructive={true}
        isLoading={isSavingCourts && isCourtSaveRemovalModalOpen}
      />

    </div>
  );
};

export default ClubConfig;